import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AlertTriangle, Ban, BellRing, Banknote, CalendarCheck, Check, CheckCircle2, Clock, Eye, FileText, History, Layers, Mail,
  MapPin, Monitor, Pencil, RefreshCw, Search, Send, Smartphone, Sparkles, Split, Store, X,
} from "lucide-react";
import { api, argent, chargerPdf } from "../console/api";
import { Groupe, Ligne, Pastille, Retour, Segments, TitreGroupe, Vide } from "../espace/ui";
import { BoutonsFeuille, Confirmation, Feuille } from "./Feuille";
import { decaler, fenetrePdf, iso } from "./outils";

/* Factures sur téléphone (école, 30.10.2026 ; revu le 01.11.2026 après l'essai de Jen sur
   iPhone) : les mêmes listes et les mêmes gestes que la console (Facturation), au pouce.

   · En tête, les trois chiffres du tableau de bord (« Total à facturer », « Déjà encaissé »,
     « Reste à encaisser », cours et stages) : ils viennent de /stats, la même source que le
     tableau de bord de la console et que l'accueil de l'app. Le bandeau disait « encaissés
     sur » le total des seules factures À GÉNÉRER (`total_pretes`) : « 92467.50 encaissés
     sur 1621.00 ».
   · Les dossiers suivent la LISTE de travail calculée par le serveur (`liste`), comme la
     console : une facture, une liste.
   · Une facture se relit (son décompte, ligne par ligne) et se modifie AVANT de partir :
     rabais, ajustement, taxe administrative, mode de paiement, notes, adresse — avec les
     routes de la fenêtre « Modifier » de la console ; le total se recalcule chez le serveur
     (/facturation/montants/apercu). Un montant enregistré refait la facture (même numéro,
     nouveau QR-code), comme dans la console : elle attend dans « À envoyer ».
   · Chaque geste passe par une feuille du bas (Feuille.jsx), au-dessus de la barre d'onglets.
   · Les factures de STAGES sont dans les mêmes dossiers que celles des cours (Jen, 02.11.2026 :
     « toutes les factures à générer doivent être là, qu'elles soient pour stage ou
     semestre »), avec l'étiquette violette « Stage » et le nom du stage. Elles viennent de
     /stages, comme la puce « Stages » de la console, et ne dépendent pas du semestre affiché.
     Leurs gestes sont ceux que la console a pour un stage, avec SES routes (`/stages/…`) :
     créer, envoyer, relancer, déjà remise, voir le PDF, modifier (rabais, ajustement, mode
     de paiement), encaisser et annuler des espèces, paiement en plusieurs fois (au comptoir,
     ou après un paiement partiel). Un geste de lot prend cours et stages ensemble : chacun
     part par sa route, et le bilan est unique. Restent sur ordinateur : les jours suivis
     (prorata), « Encaissé (banque) », refaire la facture, un tarif manquant au catalogue.
   · Les factures de COURS PRIVÉS (annulé moins de 24 h avant, absence) aussi, étiquette rose
     « Cours privé » : déjà parties avec l'e-mail d'annulation, elles vont dans « En attente
     de paiement », « À relancer » (30 jours après l'envoi, puis tous les 30 jours), « Payées »
     et « Toutes » — comme le compteur des impayés de l'accueil, qui les compte (02.11.2026).
     Leurs gestes sont ceux de Facturation › Cours privés : le PDF, le rappel, payée à l'école
     (cash, TWINT), annuler la facture (/reservations-privees/factures/…).
   Ce qui reste sur ordinateur est dit en clair, avec un lien vers la console. */

// Les mêmes libellés que les badges de la console (Facturation.jsx, STATUTS).
const ETATS = {
  a_generer: ["À générer", "#2563EB", "#EFF6FF"],
  generee: ["À envoyer", "#15803D", "#F0FDF4"],
  envoyee: ["En attente de paiement", "#B45309", "#FFFBEB"],
  partielle: ["Partiel", "#B45309", "#FFFBEB"],
  payee: ["Payée", "#15803D", "#F0FDF4"],
  especes: ["Cash / espèces", "#B45309", "#FFFBEB"],
  non_facturee: ["Non facturée", "#475569", "#F1F5F9"],
  annulee: ["Annulée", "#475569", "#F1F5F9"],
  bloquee: ["À corriger", "#B91C1C", "#FEF2F2"],
};
const Etat = ({ statut }) => {
  const [label, couleur, fond] = ETATS[statut] || [statut, "#475569", "#F1F5F9"];
  return <span className="inline-block text-[11.5px] font-semibold rounded-full px-2 py-0.5 whitespace-nowrap" style={{ color: couleur, background: fond }}>{label}</span>;
};

/* La liste de travail d'une facture est décidée par le serveur (`liste`) ; un état inconnu
   tombe dans « À corriger », comme dans la console : il se voit au lieu de disparaître. */
const LISTES_CONNUES = ["a_generer", "generee", "envoyee", "payee", "non_facturee", "bloquee", "especes"];
const listeDe = (f) => (LISTES_CONNUES.includes(f.liste) ? f.liste : "bloquee");
const DOSSIERS = {
  a_generer: { label: "À générer", aide: "Pas encore de PDF : relire, modifier, créer", Icone: Sparkles, couleur: "#2563EB", test: (f) => listeDe(f) === "a_generer" },
  generee: { label: "À envoyer", aide: "PDF prêt, pas encore parti", Icone: Send, couleur: "#15803D", test: (f) => listeDe(f) === "generee" },
  a_relancer: { label: "À relancer", aide: "En retard de paiement : un rappel est dû", Icone: BellRing, couleur: "#DC2626", test: (f) => !!f.relance?.due },
  bloquee: { label: "À corriger", aide: "Un montant ou une information à revoir", Icone: AlertTriangle, couleur: "#B91C1C", test: (f) => listeDe(f) === "bloquee" },
  envoyee: { label: "En attente de paiement", aide: "Envoyées, pas encore soldées", Icone: Clock, couleur: "#B45309", test: (f) => listeDe(f) === "envoyee" },
  echelonnes: { label: "Paiements échelonnés", aide: "En 2 ou 3 fois", Icone: Split, couleur: "#1D4ED8", test: (f) => (f.echelonnement_nb || 0) > 0 },
  payee: { label: "Payées", aide: "Soldées", Icone: CheckCircle2, couleur: "#15803D", test: (f) => listeDe(f) === "payee" },
  non_facturee: { label: "Non facturées", aide: "Total nul ou réglées autrement", Icone: Layers, couleur: "#64748B", test: (f) => listeDe(f) === "non_facturee" },
  toutes: { label: "Toutes", aide: "Les factures du semestre, des stages et des cours privés", Icone: FileText, couleur: "#0F1B2D", test: () => true },
};
// « À corriger » juste après « À générer », comme dans la console (Jen, 02.11.2026).
const A_FAIRE = ["a_generer", "bloquee", "generee", "a_relancer"];
const SUIVI = ["envoyee", "echelonnes", "payee", "non_facturee", "toutes"];

/* « Renvoyer la facture » : partie et pas soldée — la règle du serveur
   (console/renvoi_facture.py) et de la console. */
const RENVOYABLES = ["pas_echue", "echelonne_a_jour", "relancee_recemment", "report", "en_retard", "partiel", "mensualite"];
const renvoyable = (f) => listeDe(f) === "envoyee" && !!(f.numero || f.pdf_numero) && RENVOYABLES.includes(f.relance?.code);
// Ce que la ligne dit de la relance : jamais pour une facture payée, pas partie ou annulée.
const MOTIFS_MUETS = ["payee", "pas_envoyee", "annulee"];
const motifVisible = (f) => !!f.relance?.badge && !MOTIFS_MUETS.includes(f.relance.code);
const cash = (f) => !!(f.especes || f.especes_annoncees || f.especes_a_confirmer || /cash|esp/i.test(f.mode_paiement || ""));
const reste = (f) => Math.max(Math.round(((f.total || 0) - (f.recu || 0)) * 100) / 100, 0);
const ids = (f) => f.facturation_ids || (f.postes || []).map((p) => p.id);
const jour = (iso) => (iso ? String(iso).slice(0, 10).split("-").reverse().join(".") : "");
const aujourdhui = () => iso(new Date());   // le jour en Suisse, pas en temps universel
const nombre = (v) => (v === "" || v === null || v === undefined ? 0 : Number(String(v).replace(",", ".").replace("−", "-")) || 0);
const lisible = (v) => Number.isFinite(Number(String(v).replace(",", ".")));
const signe = (v) => (v > 0.005 ? `+ ${argent(v)}` : v < -0.005 ? `− ${argent(-v)}` : argent(0));
// « la facture » / « les 3 factures ».
const nFactures = (n) => (n > 1 ? `les ${n} factures` : "la facture");
// Au comptoir : les cartes 10 cours et les workshops se paient en espèces ou en TWINT.
const HORS_FACTURE = ["carte_10", "workshop"];
const GENRES = { facture: "Cours", stage: "Stage", carte_10: "Carte 10 cours", workshop: "Workshop heels" };

/* ── Les factures de stages ──────────────────────────────────────────────────────────────
   Une ligne de /stages devient une facture comme les autres, comme dans la console
   (Facturation.jsx, versLigne) : mêmes dossiers, mêmes lignes, et seules les routes changent
   — d'où `genre`. Son décompte (`recap`) est celui que son PDF imprime : le tarif du
   catalogue de chaque enfant, puis prorata, rabais et ajustement, chacun sa ligne, tels que
   le serveur les a comptés. */
const estStage = (f) => f?.genre === "stage";
// Payée en partie, elle attend le reste dans « En attente de paiement » (comme la console).
const listeDuStage = (l) => (l.statut === "partielle" ? "envoyee" : (l.statut || "a_generer"));
const versLigneStage = (l) => ({
  ...l,
  genre: "stage",
  cle: `stage::${l.cle}`,
  cle_stage: l.cle,
  pdf: !!l.numero,
  pdf_numero: l.numero || "",
  pdf_version: l.numero ? 1 : 0,
  eleves: (l.enfants || []).map((e) => e.eleve),
  liste: listeDuStage(l),
  // Le nom du stage est déjà en tête de la fiche : la ligne dit le tarif retenu pour l'enfant.
  recap: {
    eleves: (l.enfants || []).map((e) => ({ eleve_id: e.inscription_id, eleve: e.eleve,
      lignes: [{ libelle: "Tarif du stage", detail: /catalogue/.test(e.regle || "") ? (e.membre ? "membre" : "non-membre") : e.regle, montant: e.montant }] })),
    famille: (l.corrections || []).map((c) => ({ libelle: c.libelle, montant: c.montant, cle: c.genre || "autre" })),
  },
});

/* ── Les factures de cours privés ─────────────────────────────────────────────────────────
   Celles de Facturation › Cours privés (/reservations-privees/factures) : une facture par
   cours annulé moins de 24 h avant ou manqué, partie avec l'e-mail de l'école, suivie comme
   les autres (relances.eligibilite, un rappel tous les 30 jours). Jamais « à générer » ni « à
   envoyer » : elle attend son paiement (« En attente de paiement »), puis « Payées ». */
const estPrive = (f) => f?.genre === "prive";
const estCours = (f) => !f?.genre;
// Le motif d'une facture de cours privé : celui écrit par le serveur (« annulé moins de 24 h
// avant » avec le seuil de CETTE facture, Réglages › Cours privés), sinon celui de départ.
const MOTIFS_PRIVE = { tardive: "annulé moins de 24 h avant", absence: "absent·e sans annulation" };
const STATUTS_PRIVE = { "Payée": "payee", "Partiellement payée": "partielle", "Annulée": "annulee" };
const versLignePrive = (p, joursRappel = 30) => {
  const statut = STATUTS_PRIVE[p.statut] || "envoyee";
  const total = statut === "annulee" ? 0 : Number(p.total_a_facturer || 0);
  return {
    ...p,
    genre: "prive",
    cle: `prive::${p.no_facture}`,
    numero: p.no_facture,
    pdf: true,
    pdf_numero: p.no_facture,
    pdf_version: 0,
    eleves: p.eleves && p.eleves !== p.client ? [p.eleves] : [],
    total,
    recu: Number(p.montant_recu || 0),
    statut,
    liste: statut === "payee" ? "payee" : statut === "annulee" ? "annulee" : "envoyee",
    envoyee_le: p.facture_envoyee_le || "",
    echeance: p.facture_envoyee_le ? decaler(String(p.facture_envoyee_le).slice(0, 10), joursRappel) : "",
    especes: false,
    recap: { eleves: [{ eleve_id: p.no_facture, eleve: p.eleves || p.client,
      lignes: [{ libelle: p.libelle || "Cours privé", detail: p.montant_cours ? `prix du cours ${argent(p.montant_cours)}` : "", montant: total }] }], famille: [] },
  };
};

/* L'étiquette du genre : « Stage » en violet (Jen, 02.11.2026 ; le bleu est celui
   d'« Échelonné »), suivi du nom du stage — deux stages d'une même famille se distinguent
   d'un coup d'œil ; « Cours privé » en rose, suivi de son motif. Rien pour un cours. */
const ETIQUETTES = {
  stage: { mot: "Stage", texte: "#6D28D9", fond: "#F5F3FF", bord: "#DDD6FE", suite: (f) => f.stage },
  prive: { mot: "Cours privé", texte: "#BE185D", fond: "#FDF2F8", bord: "#FBCFE8", suite: (f) => f.motif_texte || MOTIFS_PRIVE[f.motif] || MOTIFS_PRIVE.tardive },
};
// `entier` (la fiche) : le texte en entier, sur deux lignes au besoin.
const EtiquetteGenre = ({ f, testId, entier = false }) => {
  const e = ETIQUETTES[f.genre];
  if (!e) return null;
  return (
    <span className={`inline-flex ${entier ? "items-start" : "items-center"} gap-1.5 min-w-0 max-w-full`} data-testid={testId || `factures-ligne-${f.genre}`}>
      <span className="shrink-0 text-[11.5px] font-semibold rounded-full px-2 py-0.5"
        style={{ color: e.texte, background: e.fond, border: `1px solid ${e.bord}` }}>{e.mot}</span>
      <span className={`text-[12px] font-medium ${entier ? "leading-snug pt-0.5" : "truncate"}`} style={{ color: e.texte }}>{e.suite(f)}</span>
    </span>
  );
};

/* Un champ de feuille : le libellé, le champ, une aide. */
const Champ = ({ label, aide, children }) => (
  <div>
    <div className="text-[13px] font-semibold text-[var(--muted)] mb-1">{label}</div>
    {children}
    {aide && <div className="text-[12px] text-[var(--muted)] mt-1 leading-snug">{aide}</div>}
  </div>
);
const Choix = ({ options, valeur, onChange, testId }) => (
  <Segments testId={testId} valeur={valeur} onChange={onChange} options={options.map(([id, label]) => ({ id, label }))} />
);
const TONS = { info: ["#EFF6FF", "#BFDBFE", "#1E3A8A"], attention: ["#FFFBEB", "#FDE68A", "#92400E"], danger: ["#FEF2F2", "#FECACA", "#991B1B"], ok: ["#F0FDF4", "#BBF7D0", "#166534"] };
const Note = ({ ton = "info", children, testId }) => {
  const [fond, bord, texte] = TONS[ton] || TONS.info;
  return <div className="rounded-2xl px-4 py-3 text-[13.5px] leading-snug" style={{ background: fond, border: `1px solid ${bord}`, color: texte }} data-testid={testId}>{children}</div>;
};

/* Le bilan d'un geste : un titre et, au besoin, les familles concernées (pas créées, et
   pourquoi). Il reste affiché jusqu'au geste suivant. */
const Bilan = ({ b, onFermer }) => (b ? (
  <Note ton={b.ton} testId="factures-message">
    <div className="flex items-start gap-2">
      <div className="flex-1 min-w-0">
        <div className="font-semibold">{b.titre}</div>
        {b.texte && <div className="mt-0.5">{b.texte}</div>}
        {(b.lignes || []).length > 0 && (
          <ul className="mt-1 space-y-0.5 list-disc pl-4">{b.lignes.slice(0, 8).map((l, k) => <li key={k}>{l}</li>)}</ul>
        )}
      </div>
      <button type="button" onClick={onFermer} aria-label="Fermer le message" className="shrink-0 -mr-1 p-1"><X size={15} /></button>
    </div>
  </Note>
) : null);

/* Une ligne d'action : la pastille de couleur, le geste en clair, ce qu'il fait. */
const Geste = ({ Icone, couleur, titre, sousTitre, onClick, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId}
    className="w-full flex items-center gap-3 px-4 py-3 min-h-[56px] text-left active:bg-[#F1F5F9]">
    <Pastille Icone={Icone} couleur={couleur} taille={32} />
    <span className="flex-1 min-w-0">
      <span className="block text-[15.5px] font-medium leading-snug">{titre}</span>
      {sousTitre && <span className="block text-[12.5px] text-[var(--muted)] leading-snug mt-0.5 break-words">{sousTitre}</span>}
    </span>
  </button>
);
const Gestes = ({ liste }) => (
  <Groupe>
    {liste.map(([titre, Icone, couleur, onClick, sous]) => (
      <Geste key={titre} Icone={Icone} couleur={couleur} titre={titre} sousTitre={sous} onClick={onClick} testId={`factures-action-${titre}`} />
    ))}
  </Groupe>
);

/* ── La facture, ligne par ligne : le décompte du serveur (`recap`), comme la partie « La
   facture » de la fenêtre « Modifier » de la console. Rien n'est additionné ici. ───────── */
const Decompte = ({ f, recap: r, total }) => {
  const recap = r || f.recap || { eleves: [], famille: (f.decompte_lignes || []).map((l) => ({ ...l, cle: "autre" })) };
  return (
    <div className="px-4 py-3 text-[14px]" data-testid="factures-decompte">
      {recap.eleves.length === 0 && <p className="text-[var(--muted)]">Aucun cours facturé sur cette facture.</p>}
      {recap.eleves.map((b) => (
        <div key={b.eleve_id || b.eleve} className="py-1.5 border-b" style={{ borderColor: "#EEF2F6" }}>
          {recap.eleves.length > 1 && <div className="text-[12.5px] font-semibold mb-0.5">{b.eleve}</div>}
          {b.lignes.map((l, k) => (
            <div key={k} className="flex justify-between gap-3">
              <span className="min-w-0">{l.libelle}{l.detail ? <span className="text-[var(--muted)]"> · {l.detail}</span> : null}</span>
              <span className="whitespace-nowrap tabular-nums">{argent(l.montant)}</span>
            </div>
          ))}
          {Math.abs(b.prorata || 0) > 0.005 && (
            <div className="flex justify-between gap-3 text-[var(--muted)]"><span>Prorata — mois non suivis</span><span className="whitespace-nowrap">{signe(b.prorata)}</span></div>
          )}
          {Math.abs(b.accumulation || 0) > 0.005 && (
            <div className="flex justify-between gap-3" style={{ color: "#065F46" }}><span>{b.accumulation_libelle}</span><span className="whitespace-nowrap">{signe(b.accumulation)}</span></div>
          )}
          {Math.abs(b.costume || 0) > 0.005 && (
            <div className="flex justify-between gap-3 text-[var(--muted)]"><span>Costume de spectacle</span><span className="whitespace-nowrap">{signe(b.costume)}</span></div>
          )}
        </div>
      ))}
      {recap.famille.length > 0 && (
        <div className="pt-2 space-y-0.5">
          {recap.famille.map((l, k) => (
            <div key={k} className="flex justify-between gap-3">
              <span className={l.cle === "cours" ? "" : "text-[var(--muted)]"}>{l.libelle}</span>
              <span className="whitespace-nowrap tabular-nums">{l.cle === "cours" ? argent(l.montant) : signe(l.montant)}</span>
            </div>
          ))}
        </div>
      )}
      <div className="flex justify-between gap-3 mt-2 pt-2 border-t font-semibold text-[15.5px]" style={{ borderColor: "var(--border)" }}>
        <span>Total de la facture</span><span className="tabular-nums" data-testid="factures-decompte-total">{argent(total ?? f.total)}</span>
      </div>
    </div>
  );
};

/* ── Modifier la facture : les décisions de la fenêtre « Modifier » de la console ─────────
   Rabais, ajustement (+/−), taxe administrative, mode de paiement, notes. Ce qui part au
   serveur est ce que la console envoie (PATCH /facturation/poste/{id}, poste par poste,
   tous ses champs) ; le total se recalcule chez le serveur (/facturation/montants/apercu).
   Prix des cours forcé, prorata et correction d'un encaissement bancaire : console. */
const FeuilleModifier = ({ f, corps, onFermer, onFait }) => {
  const postes = f.postes || [];
  const lignesDe = (p) => (f.detail || []).filter((x) => x.poste_id === p.id);
  const avecCours = postes.filter((p) => lignesDe(p).length);
  const facturees = avecCours.length ? avecCours : postes.slice(0, 1);
  const principal = facturees[0] || postes[0];
  // Rabais, ajustement et notes valent pour LA facture : on les lit et les écrit sur la ligne
  // qui les porte déjà (ou la première inscription facturée). Plusieurs lignes en portent ?
  // Le champ se modifie dans la console (« Cas rares »), pour ne rien déplacer.
  const porteurDe = (champ, motif) => {
    const avec = postes.filter((p) => Math.abs(nombre(p[champ])) > 0.005 || String(p[motif] || "").trim());
    return avec.length > 1 ? null : (avec[0] || principal);
  };
  const pRabais = porteurDe("rabais", "motif_rabais");
  const pAjust = porteurDe("ajustement", "motif_ajustement");
  const avecNotes = postes.filter((p) => String(p.notes || "").trim());
  const pNotes = avecNotes.length > 1 ? null : (avecNotes[0] || principal);
  const modeEcrit = (postes.find((p) => String(p.mode_paiement || "").trim()) || {}).mode_paiement || "";
  const cashAuDepart = modeEcrit ? /cash|esp/i.test(modeEcrit) : !!f.especes;
  const taxeDe = (p) => nombre(p.taxe_calculee ?? p.taxe_admin);

  // Le clavier des montants de l'iPhone n'a pas de signe moins : le rabais se tape en
  // positif (« 50 » = 50 CHF de moins) et l'ajustement a son sens (ajouter / déduire).
  const [rabais, setRabais] = useState(pRabais && nombre(pRabais.rabais) ? String(Math.abs(nombre(pRabais.rabais))) : "");
  const [motifRabais, setMotifRabais] = useState(pRabais?.motif_rabais || "");
  const [sens, setSens] = useState(pAjust && nombre(pAjust.ajustement) < 0 ? "moins" : "plus");
  const [ajust, setAjust] = useState(pAjust && nombre(pAjust.ajustement) ? String(Math.abs(nombre(pAjust.ajustement))) : "");
  const [motifAjust, setMotifAjust] = useState(pAjust?.motif_ajustement || "");
  const [taxes, setTaxes] = useState(() => Object.fromEntries(postes.map((p) => [p.id, String(taxeDe(p))])));
  const [auComptoir, setAuComptoir] = useState(cashAuDepart);
  const [notes, setNotes] = useState(pNotes?.notes || "");
  const [apercu, setApercu] = useState(null);
  const [envoi, setEnvoi] = useState(false);
  const [err, setErr] = useState("");

  const modeChange = auComptoir !== cashAuDepart;
  const champsDe = (p) => {
    const taxe = nombre(taxes[p.id]);
    const taxeDecidee = Math.abs(taxe - taxeDe(p)) > 0.005;
    return {
      total_cours: nombre(p.total_cours),
      // Calculées par la console d'après les Réglages : jamais réécrites d'ici.
      reduction_accumulation: nombre(p.reduction_accumulation),
      reduction_famille: nombre(p.reduction_famille),
      taxe_admin: taxeDecidee ? taxe : nombre(p.taxe_admin),
      ...(taxeDecidee ? { taxe_admin_manuel: true } : {}),
      essais_regles: nombre(p.essais_regles),
      ajustement: p === pAjust ? (sens === "moins" ? -1 : 1) * Math.abs(nombre(ajust)) : nombre(p.ajustement),
      motif_ajustement: p === pAjust ? motifAjust : (p.motif_ajustement || ""),
      rabais: p === pRabais ? -Math.abs(nombre(rabais)) : nombre(p.rabais),
      motif_rabais: p === pRabais ? motifRabais : (p.motif_rabais || ""),
      montant_recu: nombre(p.montant_recu), date_paiement: p.date_paiement || "",
      mode_paiement: modeChange ? (auComptoir ? "Cash" : "Virement") : (p.mode_paiement || ""),
      notes: p === pNotes ? notes : (p.notes || ""),
    };
  };
  const changeDe = (p) => {
    const c = champsDe(p);
    const montant = ["taxe_admin", "ajustement", "rabais"].some((k) => Math.abs(nombre(c[k]) - nombre(p[k])) > 0.005)
      || (!!c.taxe_admin_manuel && !p.taxe_admin_manuel);
    const autre = ["motif_ajustement", "motif_rabais", "mode_paiement", "notes"].some((k) => String(c[k] || "") !== String(p[k] || ""));
    return { montant, autre };
  };
  const montantsChanges = postes.some((p) => changeDe(p).montant);
  const rienAChanger = !postes.some((p) => changeDe(p).montant || changeDe(p).autre);
  const invalide = (!!rabais && !lisible(rabais)) || (!!ajust && !lisible(ajust));

  // Le nouveau total, calculé par le SERVEUR avec ce qui serait enregistré.
  const signature = JSON.stringify(postes.map((p) => champsDe(p)));
  useEffect(() => {
    if (!montantsChanges || invalide) { setApercu(null); return undefined; }
    let fini = false;
    const minuterie = setTimeout(async () => {
      try {
        const r = await api("/facturation/montants/apercu", { method: "POST",
          body: JSON.stringify({ postes: Object.fromEntries(postes.map((p) => [p.id, champsDe(p)])) }) });
        if (!fini) setApercu(r);
      } catch (e) { if (!fini) setApercu({ ok: false, erreur: e.message }); }
    }, 400);
    return () => { fini = true; clearTimeout(minuterie); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature, montantsChanges, invalide]);

  const enregistrer = async () => {
    setEnvoi(true); setErr("");
    try {
      for (const p of postes) {
        const ch = changeDe(p);
        if (!ch.montant && !ch.autre) continue;
        await api(`/facturation/poste/${encodeURIComponent(p.id)}`, { method: "PATCH", body: JSON.stringify({ motif: "", champs: champsDe(p) }) });
      }
      // Comme la console : un montant changé refait la facture tout de suite (même numéro,
      // nouveau QR-code) ; elle attend dans « À envoyer ». Rien ne part.
      if (montantsChanges && !auComptoir) {
        const r = await api("/facturation/generer", { method: "POST", body: JSON.stringify({ refs: [f.cle], refaire: true, limite: 20, ...corps }) });
        const refus = (r.refusees || [])[0];
        await onFait(refus
          ? { ton: "attention", titre: "Modification enregistrée — la facture n'a pas pu être refaite", texte: refus.motif }
          : { ton: "ok", titre: "Facture modifiée et refaite", texte: "Même numéro, nouveau QR-code. Elle attend dans « À envoyer » : relis-la, puis envoie-la. Rien n'est parti." });
      } else {
        await onFait({ ton: "ok", titre: "Modification enregistrée",
          texte: modeChange ? (auComptoir ? "Elle se règle au comptoir : plus d'envoi ni de rappel." : "Elle repasse sur facture QR : à créer, puis à envoyer.") : "" });
      }
    } catch (e) { setErr(e.message); setEnvoi(false); }
  };

  return (
    <Feuille titre="Modifier la facture" sousTitre={`${f.client} · ${argent(f.total)}`} onFermer={onFermer} occupe={envoi} testId="factures-modifier"
      pied={(
        <div className="space-y-2">
          <div className="flex justify-between items-baseline gap-3 text-[15px] font-semibold" data-testid="factures-modifier-total">
            <span>{montantsChanges ? "Nouveau total" : "Total"}</span>
            <span className="tabular-nums text-right">
              {!montantsChanges ? argent(f.total) : !apercu ? "calcul…" : apercu.ok ? argent(apercu.total)
                : <span className="text-[13px]" style={{ color: "#B91C1C" }}>{apercu.erreur}</span>}
              {montantsChanges && apercu?.ok && Math.abs(apercu.total - (f.total || 0)) > 0.004 && (
                <span className="block text-[12px] font-normal text-[var(--muted)]">au lieu de {argent(f.total)}</span>
              )}
            </span>
          </div>
          {montantsChanges && !auComptoir && (
            <p className="text-[12.5px] text-[var(--muted)]">En enregistrant, la facture est refaite (même numéro, nouveau QR-code) et attend dans « À envoyer ». Rien ne part.</p>
          )}
          {err && <p className="text-[13px]" role="alert" style={{ color: "#B91C1C" }}>{err}</p>}
          <BoutonsFeuille onAnnuler={onFermer} onValider={enregistrer} occupe={envoi} testId="factures-modifier-enregistrer"
            desactive={rienAChanger || invalide || (montantsChanges && !!apercu && !apercu.ok)}
            libelle="Enregistrer" />
        </div>
      )}>
      {f.envoyee_le && (
        <Note ton="attention">Déjà envoyée le {jour(f.envoyee_le)}. Si un montant change, la facture est refaite et attend dans « À envoyer » : la famille garde l'ancienne tant que tu ne la renvoies pas.</Note>
      )}
      {pRabais ? (
        <>
          <Champ label="Rabais (CHF en moins)" aide="Sa propre ligne sur la facture. Vide : pas de rabais.">
            <input className="input-field w-full" inputMode="decimal" value={rabais} onChange={(e) => setRabais(e.target.value)} placeholder="0" aria-label="Rabais en francs" data-testid="factures-champ-rabais" />
          </Champ>
          <Champ label="Motif du rabais" aide="Imprimé sur la facture.">
            <input className="input-field w-full" value={motifRabais} onChange={(e) => setMotifRabais(e.target.value)} placeholder="ex. geste commercial" aria-label="Motif du rabais" data-testid="factures-champ-motif-rabais" />
          </Champ>
        </>
      ) : <Note>Cette facture porte des rabais sur plusieurs inscriptions : ils se modifient dans la console.</Note>}
      {pAjust ? (
        <>
          <Champ label="Ajustement" aide="Ajouter : un montant convenu à faire payer. Déduire : corriger une erreur. Les cours annulés sont déjà hors du total.">
            <div className="space-y-2">
              <Choix testId="factures-sens-ajustement" valeur={sens} onChange={setSens} options={[["plus", "Ajouter"], ["moins", "Déduire"]]} />
              <input className="input-field w-full" inputMode="decimal" value={ajust} onChange={(e) => setAjust(e.target.value)} placeholder="0" aria-label="Ajustement en francs" data-testid="factures-champ-ajustement" />
            </div>
          </Champ>
          {(nombre(ajust) !== 0 || motifAjust) && (
            <Champ label="Motif de l'ajustement" aide="Imprimé sur la facture.">
              <input className="input-field w-full" value={motifAjust} onChange={(e) => setMotifAjust(e.target.value)} aria-label="Motif de l'ajustement" data-testid="factures-champ-motif-ajustement" />
            </Champ>
          )}
        </>
      ) : <Note>Cette facture porte des ajustements sur plusieurs inscriptions : ils se modifient dans la console.</Note>}
      {facturees.map((p) => {
        const t = taxeDe(p);
        const v = nombre(taxes[p.id]);
        const options = [["0", "Non"], ["20", "Oui · 20 CHF"], ...([0, 20].includes(v) ? [] : [[String(v), `${v} CHF`]])];
        return (
          <Champ key={p.id} label={facturees.length > 1 ? `Taxe administrative — ${(p.eleves || []).join(" · ") || p.demande}` : "Taxe administrative"}
            aide={Math.abs(v - t) > 0.005 ? `Décision de l'école (calculée : ${argent(t)}).` : t > 0 ? "Calculée : nouvel élève, une seule fois." : "Calculée : aucune (déjà inscrit·e)."}>
            <Choix testId={`factures-taxe-${p.id}`} valeur={String(v)} onChange={(x) => setTaxes((tx) => ({ ...tx, [p.id]: x }))} options={options} />
          </Champ>
        );
      })}
      <Champ label="Mode de paiement" aide={auComptoir ? "Au comptoir : aucune facture envoyée, plus de rappel. Ce qui est déjà reçu reste compté." : "Facture QR : la famille paie par virement."}>
        <Choix testId="factures-mode" valeur={auComptoir ? "cash" : "qr"} onChange={(x) => setAuComptoir(x === "cash")} options={[["qr", "Facture QR"], ["cash", "Cash, au comptoir"]]} />
      </Champ>
      {pNotes && (
        <Champ label="Notes internes" aide="Pas sur la facture.">
          <textarea className="input-field w-full" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} aria-label="Notes internes" data-testid="factures-champ-notes" />
        </Champ>
      )}
      {montantsChanges && apercu?.ok && (
        <div>
          <div className="text-[13px] font-semibold text-[var(--muted)] mb-1">La facture après la modification</div>
          <Groupe><Decompte f={f} recap={apercu.recap} total={apercu.total} /></Groupe>
        </div>
      )}
      <p className="text-[12.5px] text-[var(--muted)]">
        Prix des cours, prorata, réductions et bons de parrainage sont calculés par la console. Forcer un prix ou un prorata, corriger un encaissement bancaire : console › Facturation › Modifier.
      </p>
    </Feuille>
  );
};

/* ── Modifier une facture de stage : la fenêtre « Facture de stage » de la console ───────
   Le prix vient du catalogue (tarif membre ou non) et ne change pas. Ici : le rabais (motif
   facultatif), l'ajustement (+/−, motif obligatoire) et le mode de paiement — les routes de
   la console (/stages/ajustement, /facturation/especes/mode). Le prorata déjà noté est
   renvoyé tel quel : les jours suivis se cochent dans la console, et le serveur refuse que
   l'app les change (/stages/jours). Une facture déjà fabriquée est refaite par le serveur,
   même numéro ; déjà partie, elle attend dans « À envoyer » pour être renvoyée. */
const FeuilleModifierStage = ({ f, onFermer, onFait }) => {
  const tarifs = f.tarifs ?? f.total ?? 0;
  const prorata = nombre(f.prorata);
  const [rabais, setRabais] = useState(nombre(f.rabais) ? String(Math.abs(nombre(f.rabais))) : "");
  const [motifRabais, setMotifRabais] = useState(f.motif_rabais || "");
  const [sens, setSens] = useState(nombre(f.ajustement) < 0 ? "moins" : "plus");
  const [ajust, setAjust] = useState(nombre(f.ajustement) ? String(Math.abs(nombre(f.ajustement))) : "");
  const [motifAjust, setMotifAjust] = useState(f.motif_ajustement || "");
  const [auComptoir, setAuComptoir] = useState(!!f.especes);
  const [envoi, setEnvoi] = useState(false);
  const [err, setErr] = useState("");

  const valRabais = -Math.abs(nombre(rabais));
  const valAjust = (sens === "moins" ? -1 : 1) * Math.abs(nombre(ajust));
  const total = Math.round((tarifs + prorata + valRabais + valAjust) * 100) / 100;
  const decompteChange = Math.abs(valRabais - nombre(f.rabais)) > 0.005 || Math.abs(valAjust - nombre(f.ajustement)) > 0.005
    || (!!valRabais && motifRabais.trim() !== String(f.motif_rabais || "").trim())
    || (!!valAjust && motifAjust.trim() !== String(f.motif_ajustement || "").trim());
  const modeChange = auComptoir !== !!f.especes;
  const invalide = (!!rabais && !lisible(rabais)) || (!!ajust && !lisible(ajust));
  const sansMotif = !!valAjust && !motifAjust.trim();
  // Un prorata noté sans ses jours (ancienne saisie) ne se renverrait pas : il serait perdu.
  // Ses montants se changent alors dans la console ; le mode de paiement, lui, reste possible.
  const prorataSansJours = Math.abs(prorata) > 0.005 && !nombre(f.jours_du_stage);
  const bloque = invalide || sansMotif || total < 0 || (!decompteChange && !modeChange) || (decompteChange && prorataSansJours);

  const enregistrer = async () => {
    setEnvoi(true); setErr("");
    try {
      let r = null;
      if (decompteChange) {
        // Les jours du prorata repartent tels que le serveur les a notés : il le garde.
        r = await api("/stages/ajustement", { method: "POST", body: JSON.stringify({
          cle: f.cle_stage, jours_suivis: nombre(f.jours_suivis), jours_total: nombre(f.jours_du_stage),
          rabais: Math.abs(valRabais), motif_rabais: valRabais ? motifRabais.trim() : "",
          ajustement: valAjust, motif: valAjust ? motifAjust.trim() : "" }) });
      }
      if (modeChange) {
        await api("/facturation/especes/mode", { method: "POST", body: JSON.stringify({ ref: `stage::${f.cle_stage}`, mode: auComptoir ? "cash" : "facture" }) });
      }
      await onFait({ ton: "ok",
        titre: r?.facture_refaite ? `Facture de stage modifiée et refaite (n° ${r.facture_refaite})` : "Facture de stage modifiée",
        texte: [r ? `Total : ${argent(r.total)}.` : "",
          r?.facture_refaite ? (r.a_renvoyer ? "Même numéro, nouveau montant : elle attend dans « À envoyer », à renvoyer à la famille." : "Même numéro, nouveau montant. Rien n'est parti.") : "",
          modeChange ? (auComptoir ? "Elle se règle au comptoir : plus d'envoi ni de rappel." : "Elle repasse sur facture QR : à créer, puis à envoyer.") : ""].filter(Boolean).join(" ") });
    } catch (e) { setErr(e.message); setEnvoi(false); }
  };

  return (
    <Feuille titre="Modifier la facture de stage" sousTitre={`${f.client} · ${f.stage}`} onFermer={onFermer} occupe={envoi} testId="factures-modifier-stage"
      pied={(
        <div className="space-y-2">
          <div className="flex justify-between items-baseline gap-3 text-[15px] font-semibold" data-testid="factures-modifier-total">
            <span>{decompteChange ? "Nouveau total" : "Total"}</span>
            <span className="tabular-nums text-right">
              {argent(decompteChange ? total : f.total)}
              {decompteChange && Math.abs(total - (f.total || 0)) > 0.004 && <span className="block text-[12px] font-normal text-[var(--muted)]">au lieu de {argent(f.total)}</span>}
            </span>
          </div>
          {total < 0 && <p className="text-[13px]" role="alert" style={{ color: "#B91C1C" }}>Le total ne peut pas être négatif : le rabais dépasse les tarifs du stage.</p>}
          {sansMotif && <p className="text-[12.5px]" style={{ color: "#B45309" }}>Un ajustement se note toujours avec son motif.</p>}
          {decompteChange && prorataSansJours && <p className="text-[12.5px]" style={{ color: "#B45309" }}>Le prorata de cette facture a été noté sans ses jours : rabais et ajustement se changent dans la console.</p>}
          {err && <p className="text-[13px]" role="alert" style={{ color: "#B91C1C" }}>{err}</p>}
          <BoutonsFeuille onAnnuler={onFermer} onValider={enregistrer} occupe={envoi} testId="factures-modifier-stage-enregistrer"
            desactive={bloque} libelle="Enregistrer" />
        </div>
      )}>
      {f.numero && (
        <Note ton="attention">La facture n° {f.numero} est déjà faite : si le montant change, elle est refaite (même numéro){f.envoyee_le ? " et attend dans « À envoyer », pour que la famille reçoive le bon montant" : ""}.</Note>
      )}
      <Champ label="Rabais (CHF en moins)" aide="Sa propre ligne sur la facture. Vide : pas de rabais.">
        <input className="input-field w-full" inputMode="decimal" value={rabais} onChange={(e) => setRabais(e.target.value)} placeholder="0" aria-label="Rabais en francs" data-testid="factures-stage-rabais" />
      </Champ>
      {nombre(rabais) !== 0 && (
        <Champ label="Motif du rabais" aide="Facultatif : imprimé sur la facture (sinon « Rabais »).">
          <input className="input-field w-full" value={motifRabais} onChange={(e) => setMotifRabais(e.target.value)} placeholder="ex. fratrie" aria-label="Motif du rabais" data-testid="factures-stage-motif-rabais" />
        </Champ>
      )}
      <Champ label="Ajustement" aide="Ajouter : un supplément convenu. Déduire : corriger une erreur.">
        <div className="space-y-2">
          <Choix testId="factures-stage-sens" valeur={sens} onChange={setSens} options={[["plus", "Ajouter"], ["moins", "Déduire"]]} />
          <input className="input-field w-full" inputMode="decimal" value={ajust} onChange={(e) => setAjust(e.target.value)} placeholder="0" aria-label="Ajustement en francs" data-testid="factures-stage-ajustement" />
        </div>
      </Champ>
      {(nombre(ajust) !== 0 || motifAjust) && (
        <Champ label="Motif de l'ajustement" aide="Obligatoire : imprimé sur la facture.">
          <input className="input-field w-full" value={motifAjust} onChange={(e) => setMotifAjust(e.target.value)} aria-label="Motif de l'ajustement" data-testid="factures-stage-motif-ajustement" />
        </Champ>
      )}
      <Champ label="Mode de paiement" aide={auComptoir ? "Au comptoir : aucune facture envoyée, plus de rappel. Ce qui est déjà reçu reste compté." : "Facture QR : la famille paie par virement."}>
        <Choix testId="factures-stage-mode" valeur={auComptoir ? "cash" : "qr"} onChange={(x) => setAuComptoir(x === "cash")} options={[["qr", "Facture QR"], ["cash", "Cash, au comptoir"]]} />
      </Champ>
      <p className="text-[12.5px] text-[var(--muted)]" data-testid="factures-stage-prorata">
        {Math.abs(prorata) > 0.005 ? `Prorata noté : ${signe(prorata)} (gardé tel quel). ` : ""}
        Un enfant qui ne vient pas tous les jours : les jours suivis se cochent dans la console (Facturation › Stages › Modifier), le prorata suit.
      </p>
    </Feuille>
  );
};

/* ── Encaisser : tout le reste ou une partie — « Encaisser » de la console (Cash) ────────── */
const FeuilleEncaisser = ({ it, onFermer, onFait }) => {
  const part = it.echelonnement?.part ? Math.min(it.echelonnement.part, it.reste) : null;
  const [partiel, setPartiel] = useState(!!part && part < it.reste - 0.02);
  const [montant, setMontant] = useState(part ? String(part) : "");
  const [le, setLe] = useState(aujourdhui());
  const [mode, setMode] = useState("Espèces");
  const [envoi, setEnvoi] = useState(false);
  const [err, setErr] = useState("");
  const m = partiel ? Number(String(montant).replace(",", ".")) : it.reste;
  const valide = Number.isFinite(m) && m > 0 && m <= it.reste + 0.02;
  const valider = async () => {
    setEnvoi(true); setErr("");
    try {
      const r = await api("/facturation/especes/encaisser", { method: "POST", body: JSON.stringify({
        ref: it.ref, montant: partiel ? m : null, date_paiement: le, mode: HORS_FACTURE.includes(it.genre) ? mode : "" }) });
      await onFait({ ton: "ok", titre: `Encaissé : ${argent(r.montant)}`,
        texte: partiel && m < it.reste - 0.02 ? `Il reste ${argent(it.reste - m)} à encaisser.` : "Plus aucun rappel ne part pour ce montant." });
    } catch (e) { setErr(e.message); setEnvoi(false); }
  };
  return (
    <Feuille titre={`Encaisser · ${it.titre}`} sousTitre={it.quoi} onFermer={onFermer} occupe={envoi} testId="factures-encaisser"
      pied={(
        <div className="space-y-2">
          {err && <p className="text-[13px]" role="alert" style={{ color: "#B91C1C" }}>{err}</p>}
          <BoutonsFeuille onAnnuler={onFermer} onValider={valider} occupe={envoi} desactive={!valide} testId="factures-encaisser-valider"
            libelle={valide ? `Encaisser ${argent(m)}` : "Encaisser"} />
        </div>
      )}>
      <div className="grid grid-cols-3 gap-2">
        {[["Total", it.total, "var(--text)"], ["Déjà reçu", it.recu, "#15803D"], ["Reste", it.reste, "#B45309"]].map(([t, v, c]) => (
          <div key={t} className="rounded-2xl px-3 py-2" style={{ background: "#F8FAFC", border: "1px solid var(--border)" }}>
            <div className="text-[11.5px] text-[var(--muted)]">{t}</div>
            <div className="text-[16px] font-semibold tabular-nums whitespace-nowrap" style={{ color: c }}>{argent(v)}</div>
          </div>
        ))}
      </div>
      <Choix testId="factures-encaisser-quoi" valeur={partiel ? "partie" : "tout"} onChange={(v) => setPartiel(v === "partie")}
        options={[["tout", "Tout le reste"], ["partie", "Une partie"]]} />
      {partiel && (
        <Champ label="Montant reçu aujourd'hui (CHF)" aide={`Jamais plus que le reste (${argent(it.reste)}).`}>
          <input className="input-field w-full" inputMode="decimal" value={montant} onChange={(e) => setMontant(e.target.value)} aria-label="Montant reçu" data-testid="factures-montant" />
        </Champ>
      )}
      <Champ label="Date"><input type="date" className="input-field w-full" value={le} onChange={(e) => setLe(e.target.value)} aria-label="Date du paiement" /></Champ>
      {HORS_FACTURE.includes(it.genre) ? (
        <Champ label="Payé en"><Choix testId="factures-encaisser-mode" valeur={mode} onChange={setMode} options={[["Espèces", "Espèces"], ["TWINT", "TWINT"]]} /></Champ>
      ) : (
        <p className="text-[12.5px] text-[var(--muted)]">Argent reçu au comptoir, en espèces. Un virement se pointe avec le relevé bancaire (console).</p>
      )}
    </Feuille>
  );
};

/* ── L'adresse de la facture : la fiche famille, avec la route de la console ─────────────── */
const FeuilleAdresse = ({ f, corps, onFermer, onFait }) => {
  const [rue, setRue] = useState(f.adresse || "");
  const [npa, setNpa] = useState(f.npa || "");
  const [localite, setLocalite] = useState(f.localite || "");
  const [envoi, setEnvoi] = useState(false);
  const [err, setErr] = useState("");
  // Une facture déjà fabriquée porte l'ancienne adresse sur son bulletin : elle est refaite.
  const pdfARefaire = listeDe(f) === "generee";
  const valider = async () => {
    setEnvoi(true); setErr("");
    try {
      await api(`/familles/${encodeURIComponent(f.famille_id)}`, { method: "PATCH", body: JSON.stringify({ champs: { adresse: rue.trim(), npa: npa.trim(), localite: localite.trim() } }) });
      if (pdfARefaire) {
        const r = await api("/facturation/generer", { method: "POST", body: JSON.stringify({ refs: [f.cle], refaire: true, limite: 20, ...corps }) });
        const refus = (r.refusees || [])[0];
        await onFait(refus ? { ton: "attention", titre: "Adresse enregistrée — la facture n'a pas pu être refaite", texte: refus.motif }
          : { ton: "ok", titre: "Adresse enregistrée", texte: "La facture a été refaite avec la nouvelle adresse (même numéro). Rien n'est parti." });
      } else {
        await onFait({ ton: "ok", titre: "Adresse enregistrée", texte: "Sur la fiche de la famille : elle vaut pour ses factures." });
      }
    } catch (e) { setErr(e.message); setEnvoi(false); }
  };
  return (
    <Feuille titre="Adresse de la facture" sousTitre={`${f.client} · la fiche de la famille`} onFermer={onFermer} occupe={envoi} testId="factures-adresse"
      pied={(
        <div className="space-y-2">
          {err && <p className="text-[13px]" role="alert" style={{ color: "#B91C1C" }}>{err}</p>}
          <BoutonsFeuille onAnnuler={onFermer} onValider={valider} occupe={envoi} desactive={!rue.trim() || !/^\d{4}$/.test(npa.trim()) || !localite.trim()}
            testId="factures-adresse-valider" libelle="Enregistrer" />
        </div>
      )}>
      <Champ label="Rue et numéro"><input className="input-field w-full" value={rue} onChange={(e) => setRue(e.target.value)} autoComplete="street-address" aria-label="Rue et numéro" data-testid="factures-champ-rue" /></Champ>
      <div className="grid grid-cols-[110px_1fr] gap-2">
        <Champ label="NPA"><input className="input-field w-full" inputMode="numeric" maxLength={4} value={npa} onChange={(e) => setNpa(e.target.value)} aria-label="NPA" data-testid="factures-champ-npa" /></Champ>
        <Champ label="Localité"><input className="input-field w-full" value={localite} onChange={(e) => setLocalite(e.target.value)} aria-label="Localité" data-testid="factures-champ-localite" /></Champ>
      </div>
      <p className="text-[12.5px] text-[var(--muted)]">Le bulletin de versement exige une adresse complète (NPA à 4 chiffres).{pdfARefaire ? " La facture déjà prête est refaite avec la nouvelle adresse." : ""}</p>
    </Feuille>
  );
};

/* ── Déjà partie autrement (remise en main propre) : le « Cas particulier » de la console ──
   Un stage a sa route (/stages/deja-envoyee), comme dans la fenêtre du stage de la console. */
const FeuilleDejaEnvoyee = ({ f, corps, onFermer, onFait }) => {
  const [le, setLe] = useState(aujourdhui());
  const [envoi, setEnvoi] = useState(false);
  const [err, setErr] = useState("");
  const valider = async () => {
    setEnvoi(true); setErr("");
    try {
      if (estStage(f)) await api("/stages/deja-envoyee", { method: "POST", body: JSON.stringify({ cle: f.cle_stage, date_envoi: le }) });
      else await api("/facturation/deja-envoyee", { method: "POST", body: JSON.stringify({ cles: [f.cle], date_envoi: le, ...corps }) });
      await onFait({ ton: "ok", titre: "Notée comme envoyée", texte: `Aucun e-mail n'est parti. Le délai de paiement part du ${jour(le)}.` });
    } catch (e) { setErr(e.message); setEnvoi(false); }
  };
  return (
    <Feuille titre="Déjà remise à la famille ?" sousTitre="En main propre, ou envoyée hors de la console." onFermer={onFermer} occupe={envoi} testId="factures-deja-envoyee"
      pied={(
        <div className="space-y-2">
          {err && <p className="text-[13px]" role="alert" style={{ color: "#B91C1C" }}>{err}</p>}
          <BoutonsFeuille onAnnuler={onFermer} onValider={valider} occupe={envoi} testId="factures-deja-envoyee-valider" libelle="Marquer envoyée" />
        </div>
      )}>
      <Champ label="Date d'envoi" aide="Aucun e-mail ne partira : la facture sort de « À envoyer » et son délai de paiement part de cette date.">
        <input type="date" className="input-field w-full" value={le} onChange={(e) => setLe(e.target.value)} aria-label="Date d'envoi" />
      </Champ>
    </Feuille>
  );
};

/* ── Paiement en plusieurs fois ───────────────────────────────────────────────────────────
   Un stage passe par la route de la console pour les stages (Cash / espèces et Écarts de
   paiement : /facturation/especes/echelonner, 0 = annuler) : le premier versement tombe
   aujourd'hui, puis un par mois. */
const FeuilleEchelonner = ({ f, onFermer, onFait }) => {
  const stage = estStage(f);
  const [nb, setNb] = useState(String(f.echelonnement_nb || f.ecart_suggestion_nb || 2));
  const [note, setNote] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const [err, setErr] = useState("");
  const enregistrer = async (annuler) => {
    setEnvoi(true); setErr("");
    try {
      // Les adresses écrites en entier : la recette vérifie que chaque bouton trouve sa route.
      if (stage) {
        await api("/facturation/especes/echelonner", { method: "POST", body: JSON.stringify({ ref: `stage::${f.cle_stage}`, nb: annuler ? 0 : Number(nb) }) });
      } else {
        const body = JSON.stringify({ ids: ids(f), nb: Number(nb), note });
        await (annuler ? api("/facturation/echelonnement/annuler", { method: "POST", body })
          : api("/facturation/echelonnement", { method: "POST", body }));
      }
      await onFait({ ton: "ok", titre: annuler ? "Accord annulé" : `Paiement en ${nb} fois enregistré`,
        texte: annuler ? "La facture reprend son échéance habituelle."
          : stage && cash(f) ? "Chaque enveloppe s'encaisse avec « Encaisser des espèces »." : "Aucun rappel tant que chaque versement arrive à temps." });
    } catch (e) { setErr(e.message); setEnvoi(false); }
  };
  return (
    <Feuille titre="Paiement en plusieurs fois" sousTitre={`${f.client} · ${argent(f.total)}${f.recu ? ` · déjà reçu ${argent(f.recu)}` : ""}`} onFermer={onFermer} occupe={envoi} testId="factures-echelonner"
      pied={(
        <div className="space-y-2">
          {err && <p className="text-[13px]" role="alert" style={{ color: "#B91C1C" }}>{err}</p>}
          <BoutonsFeuille onAnnuler={onFermer} onValider={() => enregistrer(false)} occupe={envoi} testId="factures-echelonner-valider" libelle="Enregistrer l'accord" />
          {f.echelonnement_nb > 0 && (
            <button type="button" className="w-full min-h-[46px] text-[15px] font-semibold" style={{ color: "#DC2626" }} disabled={envoi}
              onClick={() => enregistrer(true)} data-testid="factures-echelonner-annuler">Annuler l'accord</button>
          )}
        </div>
      )}>
      <Choix testId="factures-echelonner-nb" valeur={nb} onChange={setNb} options={[["2", "2 fois"], ["3", "3 fois"]]} />
      <p className="text-[13px] text-[var(--muted)]">
        Environ <strong>{argent(f.total ? f.total / Number(nb) : 0)}</strong> par versement. {stage
          ? `Le premier aujourd'hui, puis un par mois. ${cash(f) ? "Chaque enveloppe s'encaisse avec « Encaisser des espèces »." : "Chaque versement arrive par le relevé bancaire ; tant que l'accord est tenu, aucun rappel ne part."}`
          : "Le premier tombe à l'échéance de la facture, les suivants de mois en mois."}
        {f.echelonnement_nb > 0 && (f.echelonnement_dates || []).length ? ` Échéances en cours : ${f.echelonnement_dates.map(jour).join(" · ")}.` : ""}
      </p>
      {!stage && <Champ label="Ce qui a été convenu (facultatif)"><input className="input-field w-full" value={note} onChange={(e) => setNote(e.target.value)} aria-label="Ce qui a été convenu" /></Champ>}
    </Feuille>
  );
};

/* ── Les versions précédentes d'une facture refaite ─────────────────────────────────────── */
const FeuilleVersions = ({ f, onFermer, ouvrirPdf }) => {
  const [v, setV] = useState(null);
  useEffect(() => {
    api(`/facturation/versions/${encodeURIComponent(f.pdf_numero || f.numero)}`).then(setV).catch(() => setV({ versions: [] }));
  }, [f.pdf_numero, f.numero]);
  return (
    <Feuille titre={`Historique · n° ${f.numero || f.pdf_numero}`} sousTitre="Chaque version précédente, telle qu'elle est partie." onFermer={onFermer} testId="factures-versions">
      {!v ? <p className="text-[14px] text-[var(--muted)]">Chargement…</p> : (
        <Groupe>
          {f.pdf && <Geste Icone={Eye} couleur="#0F1B2D" titre={`Version ${f.pdf_version} — actuelle`} sousTitre={`${argent(f.total)}${f.envoyee_le ? ` · envoyée le ${jour(f.envoyee_le)}` : " · pas encore envoyée"}`}
            onClick={() => ouvrirPdf(`/facturation/pdf/${encodeURIComponent(f.pdf_numero)}`)} />}
          {v.versions.map((x) => (
            <Geste key={x.id} Icone={History} couleur="#64748B" titre={`Version ${x.version}`}
              sousTitre={`${argent(x.total)} · ${x.envoyee_le ? `envoyée le ${jour(x.envoyee_le)}` : "jamais envoyée"} · remplacée le ${jour(x.archive_le)}`}
              onClick={() => ouvrirPdf(`/facturation/version/${encodeURIComponent(x.id)}/pdf`)} />
          ))}
          {v.archive && <Geste Icone={History} couleur="#B45309" titre="Facture d'origine" sousTitre={`${argent(v.archive.total)} · reçue avant la console`}
            onClick={() => ouvrirPdf(`/factures/archive/pdf?numero=${encodeURIComponent(v.archive.numero)}`)} />}
          {!v.versions.length && !v.archive && !f.pdf && <div className="px-4 py-3 text-[14px] text-[var(--muted)]">Cette facture n'a jamais été refaite.</div>}
        </Groupe>
      )}
    </Feuille>
  );
};

/* ── Une ligne de liste ────────────────────────────────────────────────────────────────── */
/* `choix` (listes avec un geste de lot) : le rond à gauche coche ou décoche la famille ;
   le reste de la ligne ouvre la facture, comme partout ailleurs. */
const LigneFacture = ({ f, onClick, avecEtat = false, choix = null }) => {
  const r = reste(f);
  const l = listeDe(f);
  const indice = l === "a_generer" && f.adresse_incomplete ? ["adresse incomplète", "#B91C1C"]
    : motifVisible(f) ? [f.relance.badge, f.relance.due ? "#B91C1C" : "#64748B"]
      : l === "generee" && f.pdf_genere_le ? [`prête depuis le ${jour(f.pdf_genere_le)}`, "#64748B"]
        : l === "bloquee" ? [f.a_corriger_pourquoi || f.motif || "à revoir", "#B91C1C"] : null;
  const ligne = (
    <button type="button" onClick={onClick} className={`w-full flex items-center gap-3 ${choix ? "pl-1 pr-4" : "px-4"} py-3 text-left active:bg-[#F1F5F9]`} data-testid="factures-ligne">
      <div className="min-w-0 flex-1">
        <div className="text-[15.5px] font-semibold truncate">{f.client}</div>
        <div className="text-[12.5px] text-[var(--muted)] truncate">{(f.eleves || []).join(", ") || f.email}</div>
        {/* Sur deux lignes au besoin : « échéance du 06.10 — première facture » était coupé. */}
        {indice && <div className="text-[12px] font-medium line-clamp-2 mt-0.5" style={{ color: indice[1] }}>{indice[0]}</div>}
        {/* Un paiement échelonné se voit dès la liste : une étiquette bleue (Jen, 02.11.2026).
            Une mensualité en retard, c'est la ligne rouge juste au-dessus qui le dit. */}
        {((avecEtat || f.statut !== l) || f.echelonnement_nb > 0 || !estCours(f)) && (
          <div className="mt-1 flex flex-wrap items-center gap-1.5">
            <EtiquetteGenre f={f} />
            {(avecEtat || f.statut !== l) && <Etat statut={f.statut} />}
            {f.echelonnement_nb > 0 && (
              <span className="inline-flex items-center gap-1 text-[12px] font-semibold rounded-full px-2.5 py-0.5"
                style={{ background: "#EFF6FF", color: "#1D4ED8" }} data-testid="factures-ligne-echelonne">
                <Split size={12} /> Échelonné {f.echelonnement_nb}× · {f.echelonnement_recus || 0}/{f.echelonnement_nb} reçu{(f.echelonnement_recus || 0) > 1 ? "s" : ""}
              </span>
            )}
          </div>
        )}
      </div>
      <div className="text-right shrink-0">
        <div className="text-[15px] font-semibold tabular-nums whitespace-nowrap">{argent(f.total)}</div>
        {r > 0.02 && f.recu > 0.004 && <div className="text-[12px] tabular-nums whitespace-nowrap" style={{ color: "#B45309" }}>reste {argent(r)}</div>}
      </div>
    </button>
  );
  if (!choix) return ligne;
  return (
    <div className="flex items-stretch">
      <button type="button" onClick={choix.basculer} aria-pressed={choix.cochee}
        aria-label={`${choix.cochee ? "Retirer" : "Choisir"} ${f.client}`}
        className="shrink-0 w-12 grid place-items-center active:bg-[#F1F5F9]" data-testid="factures-choix">
        <span className="w-6 h-6 rounded-full grid place-items-center"
          style={choix.cochee ? { background: choix.couleur } : { border: "2px solid #CBD5E1" }}>
          {choix.cochee && <Check size={15} strokeWidth={3} color="#fff" />}
        </span>
      </button>
      <div className="min-w-0 flex-1">{ligne}</div>
    </div>
  );
};

export const FacturesMobile = ({ cible, onCompte }) => {
  const [d, setD] = useState(null);
  const [stats, setStats] = useState(null);
  const [comptoir, setComptoir] = useState(null);
  // Les factures de stages (/stages) et de cours privés : elles ne dépendent pas du semestre affiché.
  const [stagesLus, setStagesLus] = useState(null);
  const [privesLus, setPrivesLus] = useState(null);
  const [erreur, setErreur] = useState("");
  const [bilan, setBilan] = useState(null);
  const [vue, setVue] = useState(null);          // null : l'accueil des factures ; sinon un dossier
  const [cle, setCle] = useState(null);          // la facture ouverte
  const [q, setQ] = useState("");
  const [conf, setConf] = useState(null);
  const [feuille, setFeuille] = useState(null);  // { type, f } ou { type: "encaisser", it }
  const [voirComptoir, setVoirComptoir] = useState("a_encaisser");
  // Les familles DÉCOCHÉES de la liste ouverte : tout est choisi d'office (le geste de lot
  // garde son « tout d'un coup ») ; on retire celles qu'on ne veut pas (Jen, 02.11.2026).
  const [ecartees, setEcartees] = useState([]);

  const charger = useCallback(async (semestre = 0) => {
    setErreur("");
    const p = semestre ? `semestre=${semestre}` : "";
    try {
      const [x, cpt, st, pv] = await Promise.all([
        api(`/facturation?${p}`),
        api(`/facturation/especes?${p ? `${p}&` : ""}tout=true`).catch(() => null),
        api("/stages").catch(() => null),
        api("/reservations-privees/factures").catch(() => null),
      ]);
      setD(x); setComptoir(cpt); setStagesLus(st); setPrivesLus(pv);
    } catch (e) { setErreur(e.message); }
    // Les chiffres du tableau de bord : en arrière-plan, la liste n'attend pas.
    api("/stats").then(setStats).catch(() => {});
  }, []);
  // Arrivée par une alerte qui vise une facture d'un autre semestre : ce semestre d'abord.
  const arrivee = useRef(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { charger(cible?.facture ? cible.semestre || 0 : 0); }, [charger]);

  // Toucher l'onglet déjà ouvert ramène à l'accueil des factures ; « Paiements en retard »
  // (accueil de l'app) ouvre « À relancer » ; une alerte peut viser une facture.
  useEffect(() => {
    if (!cible) return;
    if (cible.racine) { setCle(null); setVue(null); setQ(""); setBilan(null); }
    if (cible.vue === "impayes") { setCle(null); setVue("a_relancer"); }
    if (cible.facture) { setVue(null); setCle(cible.facture); if (cible.semestre && !arrivee.current) charger(cible.semestre); }
  }, [cible, charger]);
  useEffect(() => { arrivee.current = false; }, []);

  // Cours du semestre, puis stages et cours privés : une seule liste, comme « Toutes les
  // factures » de la console.
  const items = useMemo(() => [...(d?.items || []), ...(stagesLus?.items || []).map(versLigneStage),
    ...(privesLus?.factures || []).map((x) => versLignePrive(x, privesLus.jours_premier_rappel || privesLus.jours_rappel))], [d, stagesLus, privesLus]);
  const vivantes = useMemo(() => items.filter((x) => x.statut !== "annulee"), [items]);
  // Une autre liste : on repart de « tout choisi ».
  useEffect(() => { setEcartees([]); }, [vue]);
  if (erreur && !d) return <Vide>{erreur}</Vide>;
  if (!d) return <div className="py-16 grid place-items-center"><RefreshCw className="animate-spin" size={20} /></div>;

  const haut = () => document.getElementById("ecole-defilement")?.scrollTo({ top: 0 });
  const ouvrir = (c) => { setBilan(null); setCle(c); haut(); };
  const allerVue = (v) => { setBilan(null); setVue(v); setCle(null); haut(); };
  const compte = (id) => vivantes.filter(DOSSIERS[id].test).length;
  const corps = { semestre: d.semestre, saison: d.saison };
  const fige = !!d.lecture_seule;
  // Une saison close fige ses factures de cours ; stages et cours privés ne dépendent pas du semestre affiché.
  const figee = (x) => fige && estCours(x);
  const enTest = d.mode_reel === false;
  // Après un geste : la feuille se ferme, la liste se relit, le bilan s'affiche en haut.
  // Après un geste, la liste repart de « tout choisi » : les familles écartées restaient
  // écartées, et « À envoyer » disait « Aucune famille choisie » (relevé le 02.11.2026).
  const fini = async (b) => { setFeuille(null); setConf(null); setEcartees([]); setBilan(b); haut(); await charger(d.semestre); onCompte?.(); };
  const echec = (e) => { setConf(null); setBilan({ ton: "danger", titre: "Rien n'a été fait", texte: e.message }); haut(); };

  // Un nouveau geste efface le bilan du précédent.
  const demander = (c) => { setBilan(null); setConf(c); };
  const feuilleOuverte = (x) => { setBilan(null); setFeuille(x); };
  // La fenêtre s'ouvre au toucher et dit « Préparation… » le temps que le serveur fasse le
  // PDF : un aperçu recalculé prend jusqu'à 5 s, et l'écran restait blanc (Jen, 02.11.2026).
  const ouvrirPdf = (chemin, attente = { titre: "Ouverture de la facture…" }) => fenetrePdf(() => chargerPdf(chemin), attente)
    .catch((e) => setBilan({ ton: "danger", titre: "PDF indisponible", texte: e.message }));
  const apercu = (f) => ouvrirPdf(`/facturation/apercu-pdf?cle=${encodeURIComponent(f.cle)}&semestre=${f.semestre || d.semestre}&saison=${encodeURIComponent(f.saison || d.saison || "")}`,
    { titre: "Préparation de la facture…", texte: "Elle est calculée à l'instant, telle qu'elle partira. Quelques secondes." });
  // Le PDF de la facture : le dernier fabriqué, sinon la copie que la famille a reçue.
  const pdfDe = (f) => (f.pdf && f.pdf_numero ? `/facturation/pdf/${encodeURIComponent(f.pdf_numero)}`
    : !f.copie_numero ? ""
      : f.copie_source === "console" ? `/facturation/pdf/${encodeURIComponent(f.copie_numero)}`
        : f.copie_source === "version" ? `/facturation/version/${encodeURIComponent(f.copie_numero)}/pdf`
          : `/factures/archive/pdf?numero=${encodeURIComponent(f.copie_numero)}`);

  /* Le serveur refuse tout le paquet de stages quand AUCUN ne peut être créé, avec le motif
     du premier : on reprend alors un par un, pour que chaque famille ait le sien. */
  const genererStages = async (cles, produites, refusees) => {
    try {
      const r = await api("/stages/generer", { method: "POST", body: JSON.stringify({ cles }) });
      produites.push(...(r.produites || []));
      refusees.push(...(r.refusees || []));
    } catch (e) {
      if (cles.length > 1) { for (const c of cles) await genererStages([c], produites, refusees); return; }
      refusees.push({ client: items.find((x) => x.cle_stage === cles[0])?.client || cles[0], motif: e.message });
    }
  };
  /* Créer (ou refaire) les PDF : par paquets de 20, comme la console ; chaque refus dit
     pourquoi. Les stages du lot passent par leur route (/stages/generer), après les cours :
     un seul bilan (Jen, 02.11.2026). « Refaire » ne vise qu'une facture de cours. */
  const generer = (liste, refaire = false) => demander({
    titre: refaire ? `Refaire la facture de ${liste[0].client} ?` : liste.length > 1 ? `Créer ${nFactures(liste.length)} ?` : `Créer la facture de ${liste[0].client} ?`,
    texte: refaire
      ? `Une nouvelle version avec un nouveau QR-code ; l'ancienne reste dans l'historique.${liste[0].envoyee_le ? " La date d'envoi est effacée : il faudra la renvoyer." : ""}`
      : `${liste.length > 1 ? "Les PDF sont préparés (par paquets de 20)" : "Le PDF et son QR-code sont préparés"} ; rien ne part encore ${liste.length > 1 ? "aux familles" : "à la famille"}.`,
    bouton: refaire ? "Refaire la facture" : liste.length > 1 ? `Créer ${liste.length} factures` : "Créer la facture",
    // Dit d'avance ce que le serveur refusera : une adresse incomplète.
    contenu: !refaire && liste.some((x) => x.adresse_incomplete) ? (
      <Note ton="attention">Adresse incomplète, donc refusée : {liste.filter((x) => x.adresse_incomplete).map((x) => x.client).join(", ")}. Les autres sont créées.</Note>
    ) : null,
    action: async () => {
      let restants = liste.filter(estCours).map((x) => x.cle);
      const clesStages = liste.filter(estStage).map((x) => x.cle_stage);
      const produites = [], refusees = [];
      try {
        while (restants.length) {
          const r = await api("/facturation/generer", { method: "POST", body: JSON.stringify({ refs: restants, limite: 20, refaire, ...corps }) });
          produites.push(...(r.produites || []));
          refusees.push(...(r.refusees || []));
          restants = r.restants || [];
        }
        if (clesStages.length) await genererStages(clesStages, produites, refusees);
      } catch (e) {
        await fini({ ton: "danger", titre: `Interrompu : ${e.message}`, texte: `${produites.length} facture(s) créée(s) avant l'incident ; recommence pour les autres.` });
        return;
      }
      await fini({
        ton: refusees.length ? "attention" : "ok",
        titre: produites.length ? `${produites.length} facture${produites.length > 1 ? "s" : ""} ${refaire ? "refaite" : "créée"}${produites.length > 1 ? "s" : ""} : à relire, puis à envoyer` : "Aucune facture créée",
        // Le QR-code n'est relu que sur les factures de cours (le serveur des stages ne le fait pas).
        texte: refusees.length ? "Pas créées, et pourquoi :" : clesStages.length ? "Rien n'est parti." : "Code QR relu et vérifié dans chaque PDF. Rien n'est parti.",
        lignes: refusees.map((x) => `${x.client} — ${x.motif}`),
      });
    },
  });
  /* Envoyer : les cours par leur numéro (/facturation/envoyer), les stages par leur clé
     (/stages/envoyer), comme la console — un seul bilan. */
  const envoyer = (liste) => {
    const lot = liste.filter((x) => x.pdf && x.pdf_numero);
    if (!lot.length) return;
    demander({
      titre: lot.length > 1 ? `Envoyer ${nFactures(lot.length)} ?` : `Envoyer la facture de ${lot[0].client} ?`,
      texte: `${lot.length > 1 ? "Elles partent aux familles, avec leur QR-facture." : `${argent(lot[0].total)} · à ${lot[0].email}. Elle part à la famille, avec son QR-facture.`}${enTest ? " Version de test : aucun e-mail ne part vraiment." : ""}`,
      bouton: lot.length > 1 ? `Envoyer ${lot.length} factures` : "Envoyer",
      action: async () => {
        const cours = lot.filter(estCours), duStage = lot.filter(estStage);
        const envois = [], echecs = [], attestations = [];
        try {
          if (cours.length) {
            const r = await api("/facturation/envoyer", { method: "POST", body: JSON.stringify({ refs: cours.map((x) => x.pdf_numero) }) });
            envois.push(...(r.envois || []));
            echecs.push(...(r.echecs || []).map((e) => `${cours.find((x) => x.pdf_numero === e.numero)?.client || e.numero} — ${e.motif}`));
            attestations.push(...(r.attestations_pretes || []));
          }
          if (duStage.length) {
            const r = await api("/stages/envoyer", { method: "POST", body: JSON.stringify({ cles: duStage.map((x) => x.cle_stage) }) });
            envois.push(...(r.envois || []));
            echecs.push(...(r.echecs || []).map((e) => `${e.client || duStage.find((x) => x.cle_stage === e.cle)?.client || e.cle} — ${e.motif}`));
          }
        } catch (e) {
          if (!envois.length) { echec(e); return; }
          await fini({ ton: "danger", titre: `Envoi interrompu : ${e.message}`, texte: `${envois.length} facture(s) déjà partie(s) ; les autres attendent dans « À envoyer ».` });
          return;
        }
        await fini({
          ton: echecs.length ? "attention" : "ok",
          titre: `${envois.length} facture${envois.length > 1 ? "s envoyées" : " envoyée"}${enTest ? " (version de test : rien n'est parti)" : ""}`,
          texte: echecs.length ? "Pas parties, et pourquoi :" : "",
          lignes: [...echecs,
            ...attestations.filter((a) => !a.echec).map((a) => `Nouvelle attestation prête pour ${a.eleve} : à envoyer depuis la console (À traiter).`)],
        });
      },
    });
  };
  /* Les rappels : le serveur revérifie chaque famille. `voulu` : « rappel quand même » pour
     UNE famille pas en retard. Relevé bancaire pas à jour : on le dit, et on relance en
     connaissance de cause. Par paquets de 5 familles, comme la console ; les stages du lot
     ensuite, par leur route (/stages/relancer, avec leurs `cles`), puis les cours privés, un
     par un comme dans Facturation › Cours privés (…/rappel) — un seul bilan. */
  const relancer = (liste, voulu = false, forcer = false, raison = "") => demander({
    titre: forcer ? "Le relevé bancaire n'est pas à jour" : liste.length > 1 ? `Relancer ${liste.length} familles en retard ?` : voulu ? `Rappel quand même à ${liste[0].client} ?` : `Rappel de paiement à ${liste[0].client} ?`,
    texte: forcer ? `${raison || "Relevé bancaire pas importé récemment."} Une famille a peut-être déjà payé.`
      // Le rappel d'un stage dit le numéro et le montant, sans pièce jointe (/stages/relancer).
      : `${voulu ? `Pas de rappel prévu aujourd'hui : ${liste[0].relance?.motif}. ` : ""}Un e-mail par famille, ${liste.every(estStage) ? "avec le numéro de la facture" : liste.some(estStage) ? "la facture jointe, son numéro seulement pour un stage" : "la dernière facture jointe"}${liste.length === 1 && liste[0].relance?.montant ? ` (${argent(liste[0].relance.montant)} réclamés)` : ""}.${enTest ? " Version de test : aucun e-mail ne part." : ""}`,
    bouton: forcer ? "Relancer quand même" : liste.length > 1 ? `Relancer ${liste.length} familles` : "Envoyer le rappel",
    danger: forcer,
    action: async () => {
      const familles = liste.filter(estCours);
      const prives = liste.filter(estPrive);
      const clesStages = liste.filter(estStage).map((x) => x.cle_stage);
      const envois = [], ignorees = [];
      try {
        /* La route des stages ne regarde pas le relevé bancaire : on le regarde d'abord, comme
           la console avant tout rappel (Facturation.jsx, relancer). */
        if (!forcer && clesStages.length) {
          const releve = await api("/releves/dernier");
          if (!releve.valide) {
            relancer(liste, voulu, true, `${releve.pourquoi || "aucun relevé bancaire importé"}. Charge le relevé des crédits (console), ou relance quand même si tu sais qui a payé.`);
            return;
          }
        }
        for (let k = 0; k < familles.length; k += 5) {
          const r = await api("/notifications/impayes/relancer", { method: "POST", body: JSON.stringify({
            refs: familles.slice(k, k + 5).flatMap(ids), forcer, ...(voulu ? { forcer_rappel: true, quand_meme_selection: true } : {}) }) });
          envois.push(...(r.envois || []));
          ignorees.push(...(r.ignorees || []));
        }
        if (clesStages.length) {
          const r = await api("/stages/relancer", { method: "POST", body: JSON.stringify({
            cles: clesStages, ...(voulu ? { forcer_rappel: true, quand_meme_selection: true } : {}) }) });
          envois.push(...(r.envois || []));
          ignorees.push(...(r.ignorees || []));
        }
        // Le serveur refuse un rappel pas dû (400, avec sa raison) : la facture est écartée.
        for (const x of prives) {
          try {
            await api(`/reservations-privees/factures/${encodeURIComponent(x.numero)}/rappel`, { method: "POST", body: JSON.stringify({ forcer: voulu }) });
            envois.push({ client: x.client });
          } catch (e) {
            if (e.message === "session") throw e;
            ignorees.push({ client: x.client, motif: e.message.replace(/^Pas de rappel pour la facture [^:]+ : /, "") });
          }
        }
      } catch (e) {
        if (!forcer && !envois.length && /relev/i.test(e.message)) { relancer(liste, voulu, true, e.message); return; }
        await fini({ ton: "danger", titre: `Relance interrompue : ${e.message}`, texte: `${envois.length} rappel(s) déjà parti(s). Relancer les autres ne fait pas de doublon.` });
        return;
      }
      await fini({
        ton: ignorees.length ? "attention" : "ok",
        titre: envois.length ? `${envois.length} rappel${envois.length > 1 ? "s envoyés" : " envoyé"}${enTest ? " (version de test : rien n'est parti)" : ""}` : "Aucun rappel envoyé",
        texte: ignorees.length ? "Sans rappel, et pourquoi :" : "",
        lignes: ignorees.map((x) => `${x.client || x.ref || x.cle} — ${x.motif}`),
      });
    },
  });
  const renvoyer = (f) => demander({
    titre: `Renvoyer la facture de ${f.client} ?`,
    texte: `La même facture n° ${f.numero || f.pdf_numero} (même QR) repart, avec un court message « voici une copie de votre facture ». Ce n'est pas un rappel ; l'échéance ne change pas.${enTest ? " Version de test : aucun e-mail ne part." : ""}`,
    bouton: "Renvoyer",
    action: () => api("/facturation/renvoyer", { method: "POST", body: JSON.stringify({ numero: f.numero || f.pdf_numero }) })
      .then((r) => fini({ ton: "ok", titre: "Facture renvoyée", texte: r.message || "" })).catch(echec),
  });
  /* Famille au comptoir (mode « Cash ») : les deux vrais choix de la console (02.11.2026).
     `mode` « especes » = « Elle paie au comptoir » (validée sans facture QR, Cash / espèces) ;
     « qr » = « Elle paie par virement » (facture QR refaite, « À envoyer »). Sans choix, la
     validation tentait une facture QR refusée et la facture restait « À corriger ». */
  const corriger = (f, mode = "") => demander({
    titre: mode === "especes" ? `Elle paie au comptoir — ${f.client}`
      : mode === "qr" ? `Elle paie par virement — ${f.client}`
        : `Valider la facture corrigée de ${f.client} ?`,
    texte: mode === "especes"
      ? `${argent(f.total)} validés sans facture QR : la facture passe dans Cash / espèces, à encaisser au comptoir. La facture reçue avant reste dans l'historique. Rien n'est envoyé.`
      : mode === "qr"
        ? "Elle passe en « Facture QR (virement) » : la facture est refaite (même numéro, nouveau QR-code) et attend dans « À envoyer ». Rien ne part : tu la relis, puis tu l'envoies."
        : "La console applique sa correction, refait la facture (même numéro, nouveau QR-code) et la range dans « À envoyer ». Rien ne part : tu la relis, puis tu l'envoies.",
    bouton: mode === "especes" ? `Valider ${argent(f.total)}` : mode === "qr" ? "Passer en facture QR" : "Valider la correction",
    action: () => api("/facturation/corrections/appliquer", { method: "POST", body: JSON.stringify({ cles: [f.cle], ...corps, ...(mode ? { mode } : {}) }) })
      .then((r) => {
        const info = (r.corrigees || [])[0] || {};
        const ou = info.liste === "especes" ? "Cash / espèces" : DOSSIERS[info.liste]?.label;
        // La liste où la facture attend, dite par le serveur : un contrôle peut la garder
        // « À corriger » (un cours sans prix, par exemple) — on le dit, sans promettre l'envoi.
        return fini(info.refus ? { ton: "attention", titre: "Correction appliquée — la facture n'a pas pu être refaite", texte: `${info.refus} Elle attend dans « ${ou || "À corriger"} ».` }
          : info.liste === "bloquee" ? { ton: "attention", titre: "Correction appliquée — la facture reste à corriger", texte: "Un contrôle la bloque encore (raison ci-dessous) : à régler dans la console." }
            : info.comptoir ? { ton: "ok", titre: info.liste === "payee" ? "Validée — déjà réglée au comptoir" : "Validée — à encaisser au comptoir",
              texte: `Aucune facture QR n'a été refaite ni envoyée. Elle est dans « ${ou || "Cash / espèces"} ».` }
              : { ton: "ok", titre: mode === "qr" ? "Facture QR refaite" : "Correction appliquée",
                texte: info.a_renvoyer ? "Le montant a changé : relis-la, puis renvoie-la à la famille."
                  : mode === "qr" && info.liste === "generee" ? "Elle attend dans « À envoyer » : relis-la, puis envoie-la à la famille."
                    : `Elle attend dans « ${ou || "À envoyer"} ».` });
      }).catch(echec),
  });
  // Un stage au comptoir : « Pas encore payé » de la console (/stages/paiement, montant 0).
  const annulerEncaissement = (f) => demander({
    titre: `Annuler l'encaissement de ${f.client} ?`, danger: true,
    texte: estStage(f) ? "Ce qui a été encaissé au comptoir est remis à zéro : la facture du stage attend de nouveau son paiement."
      : "Les espèces notées sont retirées : la facture attend de nouveau son paiement. Un virement pointé depuis le relevé bancaire, lui, reste.",
    bouton: "Annuler l'encaissement",
    action: () => (estStage(f)
      ? api("/stages/paiement", { method: "POST", body: JSON.stringify({ cle: f.cle_stage, montant: 0 }) })
      : api("/facturation/especes/annuler", { method: "POST", body: JSON.stringify({ ref: ids(f).join(",") }) }))
      .then(() => fini({ ton: "ok", titre: "Encaissement annulé" })).catch(echec),
  });
  /* Cours privé : « Payée sur place » et « Annuler » de Facturation › Cours privés. Payée,
     elle est soldée (le reste dû) ; annulée, la famille ne doit plus rien (rien ne part). */
  const payeeSurPlace = (f, mode) => demander({
    titre: `Facture ${f.numero} payée ${mode === "twint" ? "par TWINT" : "en espèces"} ?`,
    texte: `${argent(reste(f))} reçus à l'école pour ce cours privé : la facture passe « Payée », plus aucun rappel.`,
    bouton: "Oui, c'est payé",
    action: () => api(`/reservations-privees/factures/${encodeURIComponent(f.numero)}/paiement`, { method: "POST", body: JSON.stringify({ mode }) })
      .then((x) => fini({ ton: "ok", titre: "Facture payée", texte: x.message || "" })).catch(echec),
  });
  const annulerPrive = (f) => demander({
    titre: `Annuler la facture ${f.numero} ?`, danger: true,
    texte: `${f.client} ne devra plus rien pour ce cours (${argent(f.total)}). La facture reste trouvable par la recherche, marquée « Annulée ». Rien n'est envoyé à la famille.`,
    bouton: "Annuler la facture",
    action: () => api(`/reservations-privees/factures/${encodeURIComponent(f.numero)}/annuler`, { method: "POST", body: "{}" })
      .then((x) => fini({ ton: "ok", titre: "Facture annulée", texte: x.message || "" })).catch(echec),
  });
  // La forme d'une ligne du comptoir (« stage::<clé> » pour un stage, comme la console).
  const versCash = (f) => ({ ref: estStage(f) ? `stage::${f.cle_stage}` : ids(f).join(","), genre: estStage(f) ? "stage" : "facture", titre: f.client,
    quoi: estStage(f) ? [f.stage, f.dates].filter(Boolean).join(" · ") : (f.eleves || []).join(", "),
    total: f.total || 0, recu: f.recu || 0, reste: reste(f),
    echelonnement: f.echelonnement_nb ? { nb: f.echelonnement_nb, part: f.echelonnement_part || 0 } : null });

  const fermer = () => setFeuille(null);
  const feuilles = !feuille ? null
    : feuille.type === "modifier" ? <FeuilleModifier f={feuille.f} corps={corps} onFermer={fermer} onFait={fini} />
    : feuille.type === "modifier-stage" ? <FeuilleModifierStage f={feuille.f} onFermer={fermer} onFait={fini} />
      : feuille.type === "encaisser" ? <FeuilleEncaisser it={feuille.it} onFermer={fermer} onFait={fini} />
        : feuille.type === "adresse" ? <FeuilleAdresse f={feuille.f} corps={corps} onFermer={fermer} onFait={fini} />
          : feuille.type === "deja" ? <FeuilleDejaEnvoyee f={feuille.f} corps={corps} onFermer={fermer} onFait={fini} />
            : feuille.type === "echelonner" ? <FeuilleEchelonner f={feuille.f} onFermer={fermer} onFait={fini} />
              : feuille.type === "versions" ? <FeuilleVersions f={feuille.f} onFermer={fermer} ouvrirPdf={ouvrirPdf} /> : null;
  const sheets = <>{feuilles}<Confirmation c={conf} onFermer={() => setConf(null)} /></>;
  const messages = (
    <>
      <Bilan b={bilan} onFermer={() => setBilan(null)} />
      {erreur && <Note ton="danger">{erreur}</Note>}
    </>
  );

  /* ── La fiche d'une facture ────────────────────────────────────────────────────────── */
  const f = cle ? items.find((x) => x.cle === cle) : null;
  if (f) {
    const l = listeDe(f);
    const r = reste(f);
    const stage = estStage(f);
    const fg = figee(f);
    const modifiable = !fg && ["a_generer", "generee", "envoyee", "especes", "non_facturee"].includes(l);
    const incomplete = l === "a_generer" && f.adresse_incomplete;
    // Une facture QR existe déjà (reçue ou fabriquée) : passer en facture QR la refait.
    const qrDeja = !!(f.version_perimee || f.renvoi || f.date_envoi_precedent || f.pdf || f.copie_numero);
    /* Une facture de stage : les gestes que la console a pour un stage, avec ses routes. Pas
       d'aperçu recalculé (le PDF enregistré EST la facture), pas d'adresse ni de « refaire »,
       pas de versions ; « Encaisser » quand le stage se règle au comptoir, comme la console. */
    const principauxStage = () => [
      f.pdf && f.pdf_numero && ["Voir la facture (PDF)", FileText, "#0F1B2D", () => ouvrirPdf(`/stages/pdf/${encodeURIComponent(f.pdf_numero)}`), `n° ${f.numero}`],
      modifiable && !f.envoyee_le && ["Modifier la facture", Pencil, "#7C3AED", () => feuilleOuverte({ type: "modifier-stage", f }), "Rabais, ajustement, mode de paiement"],
      l === "a_generer" && ["Créer la facture", Sparkles, "#2563EB", () => generer([f]), "Le PDF et son QR-code ; rien ne part encore", true],
      l === "generee" && f.pdf_numero && ["Envoyer à la famille", Send, "#15803D", () => envoyer([f]), f.email, true],
      // Au comptoir, c'est LE geste — aussi après un acompte (le stage passe alors « Partiel »).
      f.especes_a_confirmer && ["Encaisser des espèces", Banknote, "#B45309", () => feuilleOuverte({ type: "encaisser", it: versCash(f) }), `Reste ${argent(r)}`, true],
      l === "envoyee" && f.relance?.due && ["Rappel de paiement", BellRing, "#DC2626", () => relancer([f]), f.relance.badge, true],
      l === "envoyee" && !f.relance?.due && f.relance?.forcable && ["Rappel quand même…", BellRing, "#F59E0B", () => relancer([f], true), f.relance.motif],
      renvoyable(f) && ["Renvoyer la facture (copie)", Mail, "#0284C7", () => renvoyer(f), "La même facture, sans les mots d'un rappel"],
    ];
    /* En plusieurs fois, là où la console le propose pour un stage : au comptoir (Cash /
       espèces), après un paiement partiel sans accord (Écarts de paiement), ou un accord
       déjà pris (le changer, l'annuler). */
    const echelonnableStage = cash(f) ? r > 0.02 || f.echelonnement_nb > 0 : (f.ecart_a_verifier && r > 0.02) || f.echelonnement_nb > 0;
    const autresStage = () => [
      // Déjà partie : la modifier la refait, et il faudra la renvoyer — un geste plus rare.
      modifiable && !!f.envoyee_le && ["Modifier la facture", Pencil, "#7C3AED", () => feuilleOuverte({ type: "modifier-stage", f }), "Si le montant change, elle est refaite, puis à renvoyer"],
      ["a_generer", "generee"].includes(l) && ["Déjà remise à la famille", CalendarCheck, "#64748B", () => feuilleOuverte({ type: "deja", f }), "En main propre : aucun e-mail ne part"],
      echelonnableStage && ["Paiement en plusieurs fois", Split, "#1D4ED8", () => feuilleOuverte({ type: "echelonner", f }),
        f.echelonnement_nb ? `Échelonné ${f.echelonnement_nb}× · ${f.echelonnement_recus || 0}/${f.echelonnement_nb} reçu(s)` : "2 ou 3 versements"],
      cash(f) && f.recu > 0.004 && ["Annuler l'encaissement en espèces", Banknote, "#DC2626", () => annulerEncaissement(f), "Une erreur de saisie"],
    ];
    /* Une facture de cours privé : les gestes de Facturation › Cours privés — son PDF, le
       rappel (dû 30 jours après l'envoi, puis tous les 30 jours ; « quand même » sinon),
       payée à l'école, annulée. Ni création, ni envoi, ni modification : elle part toute
       seule avec l'e-mail d'annulation. */
    const prive = estPrive(f);
    const principauxPrive = () => [
      ["Voir la facture (PDF)", FileText, "#0F1B2D", () => ouvrirPdf(`/reservations-privees/factures/${encodeURIComponent(f.numero)}/pdf`), `n° ${f.numero}`],
      l === "envoyee" && f.relance?.due && ["Rappel de paiement", BellRing, "#DC2626", () => relancer([f]), f.relance.badge, true],
      l === "envoyee" && !f.relance?.due && f.relance?.forcable && ["Rappel quand même…", BellRing, "#F59E0B", () => relancer([f], true), f.relance.motif],
    ];
    const autresPrive = () => (l !== "envoyee" ? [] : [
      ["Payée à l'école en espèces", Banknote, "#B45309", () => payeeSurPlace(f, "cash"), `${argent(r)} reçus au comptoir`],
      ["Payée à l'école par TWINT", Smartphone, "#B45309", () => payeeSurPlace(f, "twint"), `${argent(r)} reçus par TWINT`],
      !(f.recu > 0.004) && ["Annuler la facture", Ban, "#DC2626", () => annulerPrive(f), "L'école ne fait rien payer"],
    ]);
    const principaux = (prive ? principauxPrive() : stage ? principauxStage() : [
      l === "bloquee" && f.correction && !fige && !f.especes && ["Valider la correction", CheckCircle2, "#15803D", () => corriger(f), "La facture est refaite, puis attend dans « À envoyer »", true],
      // Au comptoir : les deux vrais choix, comme la carte « À corriger » de la console.
      l === "bloquee" && f.correction && !fige && f.especes && [r > 0.02 ? `Valider ${argent(f.total)} — à encaisser au comptoir` : `Valider ${argent(f.total)} — déjà réglée au comptoir`, Banknote, "#B45309", () => corriger(f, "especes"),
        r > 0.02 ? "Elle paie au comptoir : sans facture QR, elle passe dans Cash / espèces" : "Sans facture QR : elle passe dans « Payées »", true],
      l === "bloquee" && f.correction && !fige && f.especes && [qrDeja ? `Passer en facture QR et refaire la facture (${argent(f.total)})` : `Passer en facture QR (${argent(f.total)})`, RefreshCw, "#15803D", () => corriger(f, "qr"),
        qrDeja ? "Elle paie par virement : même numéro, nouvelle version, dans « À envoyer »" : "Elle paie par virement : la facture attend dans « À générer »"],
      l === "bloquee" && ["Voir la facture corrigée (PDF)", Eye, "#0F1B2D", () => apercu(f), "Recalculée à l'instant : rien n'est enregistré"],
      l === "a_generer" && !cash(f) && ["Voir la facture à jour (aperçu)", Eye, "#0F1B2D", () => apercu(f), "Telle qu'elle partira : rien n'est enregistré"],
      // Validée au comptoir : le PDF est la facture QR reçue AVANT (autre montant), gardée pour l'historique.
      !["a_generer", "bloquee"].includes(l) && pdfDe(f) && (f.comptoir_valide
        ? ["Facture QR reçue avant (PDF)", FileText, "#0F1B2D", () => ouvrirPdf(pdfDe(f)), `${argent(f.copie_total)} · ne compte plus : elle paie au comptoir`]
        : ["Voir la facture (PDF)", FileText, "#0F1B2D", () => ouvrirPdf(pdfDe(f)), f.pdf_version > 1 ? `Version ${f.pdf_version}` : f.numero ? `n° ${f.numero}` : ""]),
      modifiable && l !== "envoyee" && ["Modifier la facture", Pencil, "#7C3AED", () => feuilleOuverte({ type: "modifier", f }), "Rabais, ajustement, taxe, mode de paiement, notes"],
      !fige && f.famille_id && ["a_generer", "generee"].includes(l) && [incomplete ? "Compléter l'adresse" : "Adresse de la facture", MapPin, incomplete ? "#DC2626" : "#0284C7", () => feuilleOuverte({ type: "adresse", f }),
        incomplete ? "Il manque le NPA ou la localité : la facture ne peut pas être créée" : [f.adresse, [f.npa, f.localite].filter(Boolean).join(" ")].filter(Boolean).join(", "), incomplete],
      l === "a_generer" && !incomplete && !fige && ["Créer la facture", Sparkles, "#2563EB", () => generer([f]), "Le PDF et son QR-code ; rien ne part encore", true],
      l === "generee" && f.pdf_numero && !fige && ["Envoyer à la famille", Send, "#15803D", () => envoyer([f]), f.email, true],
      ["generee", "envoyee", "especes"].includes(l) && r > 0.02 && !fige && ["Encaisser des espèces", Banknote, "#B45309", () => feuilleOuverte({ type: "encaisser", it: versCash(f) }), `Reste ${argent(r)}`, l === "especes"],
      l === "envoyee" && f.relance?.due && !fige && ["Rappel de paiement", BellRing, "#DC2626", () => relancer([f]), f.relance.badge, true],
      l === "envoyee" && !f.relance?.due && f.relance?.forcable && !fige && ["Rappel quand même…", BellRing, "#F59E0B", () => relancer([f], true), f.relance.motif],
      renvoyable(f) && !fige && ["Renvoyer la facture (copie)", Mail, "#0284C7", () => renvoyer(f), "La même facture, sans les mots d'un rappel"],
    ]).filter(Boolean);
    const cta = principaux.find((a) => a[5]);
    const autres = (prive ? autresPrive() : stage ? autresStage() : [
      // Déjà partie : la modifier la refait, et il faudra la renvoyer — un geste plus rare.
      modifiable && l === "envoyee" && ["Modifier la facture", Pencil, "#7C3AED", () => feuilleOuverte({ type: "modifier", f }), "Elle sera refaite, puis à renvoyer"],
      ["a_generer", "generee"].includes(l) && !fige && ["Déjà remise à la famille", CalendarCheck, "#64748B", () => feuilleOuverte({ type: "deja", f }), "En main propre : aucun e-mail ne part"],
      ["generee", "envoyee"].includes(l) && !cash(f) && !fige && ["Paiement en plusieurs fois", Split, "#1D4ED8", () => feuilleOuverte({ type: "echelonner", f }),
        f.echelonnement_nb ? `Échelonné ${f.echelonnement_nb}× · ${f.echelonnement_recus || 0}/${f.echelonnement_nb} reçu(s)` : "2 ou 3 versements"],
      ["generee", "envoyee"].includes(l) && f.pdf && !fige && ["Refaire la facture", RefreshCw, "#64748B", () => generer([f], true), "Nouvelle version, nouveau QR-code"],
      (f.pdf_version > 1 || f.pdf_archive) && ["Versions précédentes", History, "#64748B", () => feuilleOuverte({ type: "versions", f }), "Ce que la famille a reçu avant"],
      cash(f) && f.recu > 0.004 && !fige && ["Annuler l'encaissement en espèces", Banknote, "#DC2626", () => annulerEncaissement(f), "Une erreur de saisie"],
    ]).filter(Boolean);
    /* Avant l'envoi (à générer, à envoyer, à corriger), on regarde CE QU'ON FACTURE : le
       décompte vient en tête, juste au-dessus de « Créer la facture » (Jen, 02.11.2026).
       Une fois partie, c'est le paiement qui compte : total, reçu, échéance, puis les gestes,
       et le décompte en dessous. */
    const enTete = ["a_generer", "generee", "bloquee"].includes(l);
    const decompte = (
      <section>
        <TitreGroupe>La facture{f.pdf_version > 0 ? ` · version ${f.pdf_version}` : ""}</TitreGroupe>
        <Groupe><Decompte f={f} /></Groupe>
      </section>
    );
    const suivi = [
      f.recu > 0.004 && <div key="recu" className="flex justify-between gap-3"><span>Déjà reçu</span><span className="tabular-nums" style={{ color: "#15803D" }}>{argent(f.recu)}</span></div>,
      f.recu > 0.004 && r > 0.02 && <div key="reste" className="flex justify-between gap-3"><span>Reste à payer</span><strong className="tabular-nums" style={{ color: "#B45309" }}>{argent(r)}</strong></div>,
      f.envoyee_le && <div key="envoi" className="flex justify-between gap-3 text-[var(--muted)]"><span>Envoyée le</span><span>{jour(f.envoyee_le)}</span></div>,
      f.echeance && <div key="echeance" className="flex justify-between gap-3 text-[var(--muted)]"><span>Échéance</span><span style={f.relance?.due ? { color: "#B91C1C" } : undefined}>{jour(f.echeance)}</span></div>,
      f.relance_envoyee_le && <div key="rappel" className="flex justify-between gap-3 text-[var(--muted)]"><span>{f.relances_nb > 1 ? `${f.relances_nb} rappels · dernier le` : "Rappel envoyé le"}</span><span>{jour(f.relance_envoyee_le)}</span></div>,
      motifVisible(f) && <div key="motif" className="text-[13px]" style={{ color: f.relance.due ? "#B91C1C" : "#B45309" }}>{f.relance.due ? "À relancer · " : ""}{f.relance.badge}</div>,
      f.echelonnement_nb > 0 && <div key="echelon" className="text-[13px] font-medium" data-testid="factures-detail-echelonne" style={{ color: "#1D4ED8" }}>Échelonné {f.echelonnement_nb}× · {f.echelonnement_recus || 0}/{f.echelonnement_nb} reçu(s) · {argent(f.echelonnement_part)}/mois</div>,
    ].filter(Boolean);
    return (
      <div className="space-y-4" data-testid="factures-detail">
        <Retour onClick={() => { setCle(null); setBilan(null); }} label={vue && DOSSIERS[vue] ? DOSSIERS[vue].label : "Factures"} />
        <div>
          <div className="font-display text-[26px] leading-tight">{f.client}</div>
          <div className="text-[13.5px] text-[var(--muted)] mt-0.5">{[(f.eleves || []).join(", "), f.numero ? `n° ${f.numero}` : ""].filter(Boolean).join(" · ")}</div>
          {!estCours(f) && (
            <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
              <EtiquetteGenre f={f} testId={`factures-detail-${f.genre}`} entier />
              {stage && f.dates && <span className="text-[12px] text-[var(--muted)]">{f.dates}</span>}
              {prive && f.prof && <span className="text-[12px] text-[var(--muted)]">avec {f.prof}</span>}
            </div>
          )}
          {enTete && f.email && <div className="text-[12.5px] text-[var(--muted)] break-all mt-0.5">{f.email}</div>}
          <div className="mt-2"><Etat statut={f.statut} /></div>
        </div>
        {messages}
        {enTete ? decompte : (
          <Groupe>
            <div className="px-4 py-3 space-y-2 text-[14.5px]">
              <div className="flex justify-between gap-3"><span>Total</span><strong className="tabular-nums">{argent(f.total)}</strong></div>
              {suivi}
              {f.email && <div className="text-[12.5px] text-[var(--muted)] break-all">{f.email}</div>}
            </div>
          </Groupe>
        )}
        {enTete && suivi.length > 0 && <Groupe><div className="px-4 py-3 space-y-2 text-[14.5px]">{suivi}</div></Groupe>}
        {incomplete && <Note ton="danger" testId="factures-adresse-incomplete">Adresse incomplète (NPA et localité) : la facture ne peut pas être créée tant qu'elle manque.</Note>}
        {l === "bloquee" && (
          <Note ton="danger">
            <strong>À corriger :</strong> {f.a_corriger_pourquoi || f.motif || "un montant est à revoir."}
            {(f.correction?.causes || []).length > 0 && <ul className="list-disc pl-4 mt-1">{f.correction.causes.map((c, k) => <li key={k}>{c}</li>)}</ul>}
            {!f.correction && <div className="mt-1">{stage
              ? "Le prix d'un stage vient de son catalogue : à compléter sur ordinateur, console › Réglages › Stages."
              : "À corriger sur ordinateur : console › Facturation › À corriger."}</div>}
          </Note>
        )}
        {f.renvoi && <Note ton="attention">La famille a reçu la version d'avant{f.renvoi.total ? ` (${argent(f.renvoi.total)})` : ""}{f.renvoi.le ? ` le ${jour(f.renvoi.le)}` : ""} : celle-ci la remplace, à renvoyer.</Note>}
        {f.gelee && <Note>Montant gelé à {argent(f.total)} (reçu par la famille). Le calcul d'aujourd'hui donnerait {argent(f.total_calcule)} : à refaire, si besoin, dans la console.</Note>}
        {(f.avertissements || []).map((a, k) => <Note key={k} ton="attention">⚠ {a}</Note>)}
        {f.carte_10 && <Note>+ carte 10 cours hors facture : {argent(f.cartes_10_total)} à encaisser au comptoir.</Note>}
        {(f.dette_saisons_passees || 0) > 0.02 && <Note ton="danger">Encore dû d'une saison passée : {argent(f.dette_saisons_passees)}{f.dette_saisons_passees_detail ? ` (${f.dette_saisons_passees_detail})` : ""} — sur sa facture d'origine, pas ajouté à celle-ci.</Note>}
        {["especes", "non_facturee"].includes(l) && f.raison && <Note>Pourquoi : {f.raison}</Note>}
        {principaux.length > 0 && (
          <section className="space-y-3">
            <TitreGroupe>Que faire</TitreGroupe>
            {/* Le geste attendu, en grand : créer, envoyer, relancer, encaisser, corriger. */}
            {cta && (
              <button type="button" onClick={cta[3]} data-testid={`factures-action-${cta[0]}`}
                className="w-full min-h-[56px] rounded-2xl px-4 py-3 flex items-center justify-center gap-2 text-white text-[16.5px] font-semibold active:opacity-90"
                style={{ background: cta[2] }}>
                {React.createElement(cta[1], { size: 19 })} {cta[0]}
              </button>
            )}
            {principaux.filter((a) => a !== cta).length > 0 && <Gestes liste={principaux.filter((a) => a !== cta)} />}
          </section>
        )}
        {!enTete && decompte}
        {autres.length > 0 && <section><TitreGroupe>Autres gestes</TitreGroupe><Gestes liste={autres} /></section>}
        {fg && <Note>Saison terminée : les montants sont figés.</Note>}
        {sheets}
      </div>
    );
  }

  /* ── Au comptoir : la liste commune de la console (Cash / espèces) et d'À traiter ─────── */
  const aEncaisser = (comptoir?.items || []).filter((it) => it.a_confirmer !== false);
  if (vue === "comptoir") {
    const liste = (comptoir?.items || []).filter((it) => (voirComptoir === "a_encaisser" ? it.a_confirmer !== false : it.a_confirmer === false));
    return (
      <div className="space-y-4" data-testid="factures-comptoir">
        <Retour onClick={() => allerVue(null)} label="Factures" />
        <div>
          <div className="font-display text-[24px] leading-tight">Au comptoir</div>
          <p className="text-[13px] text-[var(--muted)] mt-0.5">Factures réglées en espèces, cartes 10 cours et workshops (espèces ou TWINT), dettes d'un autre semestre. Touche une ligne pour encaisser.</p>
        </div>
        {messages}
        <Segments testId="factures-comptoir-vue" valeur={voirComptoir} onChange={setVoirComptoir}
          options={[{ id: "a_encaisser", label: `À encaisser · ${aEncaisser.length}` }, { id: "encaisse", label: "Encaissé" }]} />
        {!liste.length ? <Vide>{voirComptoir === "a_encaisser" ? "Rien à encaisser : tout est arrivé." : "Rien d'encaissé en entier pour l'instant."}</Vide> : (
          <Groupe>
            {liste.map((it) => (
              <button key={it.ref} type="button" disabled={it.reste <= 0.02 || fige} onClick={() => feuilleOuverte({ type: "encaisser", it })}
                className="w-full flex items-center gap-3 px-4 py-3 text-left active:bg-[#F1F5F9]" data-testid="factures-comptoir-ligne">
                <div className="min-w-0 flex-1">
                  <div className="text-[15.5px] font-semibold truncate">{it.titre}</div>
                  <div className="text-[12.5px] text-[var(--muted)] line-clamp-2">{it.quoi}</div>
                  <div className="mt-1 flex flex-wrap gap-1.5 items-center">
                    <span className="text-[11.5px] font-semibold rounded-full px-2 py-0.5" style={{ background: "#F1F5F9", color: "#475569" }}>{GENRES[it.genre] || it.genre}</span>
                    {it.quand && it.quand !== "stage" && <span className="text-[11.5px] text-[var(--muted)]">{it.quand}</span>}
                    {it.echelonnement && <span className="text-[11.5px]" style={{ color: "#1D4ED8" }}>Échelonné {it.echelonnement.nb}× · {it.echelonnement.recus}/{it.echelonnement.nb}</span>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-[15px] font-semibold tabular-nums whitespace-nowrap" style={{ color: it.reste > 0.02 ? "#B45309" : "#15803D" }}>{it.reste > 0.02 ? argent(it.reste) : "payé"}</div>
                  {it.recu > 0.004 && it.reste > 0.02 && <div className="text-[11.5px] text-[var(--muted)] tabular-nums whitespace-nowrap">sur {argent(it.total)}</div>}
                </div>
              </button>
            ))}
          </Groupe>
        )}
        <p className="text-[12.5px] text-[var(--muted)] px-1">Un rabais sur une carte 10 cours ou un workshop, l'historique d'une ligne, un encaissement à annuler : console › Facturation › Cash / espèces.</p>
        {sheets}
      </div>
    );
  }

  /* ── Un dossier : sa liste, et son geste de lot en tête ────────────────────────────── */
  if (vue && DOSSIERS[vue]) {
    const dossier = DOSSIERS[vue];
    const tri = vue === "generee" ? (a, b) => String(b.pdf_genere_le || "").localeCompare(String(a.pdf_genere_le || ""))
      : ["envoyee", "a_relancer"].includes(vue) ? (a, b) => String(a.echeance || "9999").localeCompare(String(b.echeance || "9999")) : null;
    const liste = vivantes.filter(dossier.test);
    if (tri) liste.sort(tri);
    // Cours et stages ensemble : chaque facture du lot part ensuite par sa route.
    const possibles = (vue === "a_generer" ? liste : vue === "generee" ? liste.filter((x) => x.pdf_numero) : vue === "a_relancer" ? liste : [])
      .filter((x) => !figee(x));
    // Toutes choisies d'office ; un rond décoché retire la famille du geste de lot.
    const avecChoix = possibles.length > 1;
    const lot = possibles.filter((x) => !ecartees.includes(x.cle));
    const toutes = lot.length === possibles.length;
    const basculer = (c) => setEcartees((e) => (e.includes(c) ? e.filter((x) => x !== c) : [...e, c]));
    const choisies = toutes ? "" : " choisie" + (lot.length > 1 ? "s" : "");
    const geste = lot.length > 0 && {
      a_generer: [`Créer ${nFactures(lot.length)}${choisies}`, Sparkles, "#2563EB", () => generer(lot), "Les PDF et leur QR-code ; rien ne part encore"],
      generee: [`Envoyer ${nFactures(lot.length)}${choisies}`, Send, "#15803D", () => envoyer(lot), "Aux familles, avec leur QR-facture"],
      a_relancer: [lot.length > 1 ? `Relancer les ${lot.length} familles${choisies}` : toutes ? "Relancer la famille" : "Relancer la famille choisie", BellRing, "#DC2626", () => relancer(lot), lot.some(estStage) ? "Un rappel par famille (la facture jointe, sauf pour un stage)" : "Un rappel par famille, la facture jointe"],
    }[vue];
    return (
      <div className="space-y-4" data-testid={`factures-liste-${vue}`}>
        <Retour onClick={() => allerVue(null)} label="Factures" />
        <div>
          <div className="font-display text-[24px] leading-tight">{dossier.label}</div>
          <div className="text-[13px] text-[var(--muted)]">{liste.length} facture{liste.length > 1 ? "s" : ""} · {argent(liste.reduce((s, x) => s + (x.total || 0), 0))}</div>
        </div>
        {messages}
        {geste && (
          <Groupe>
            <Geste Icone={geste[1]} couleur={geste[2]} titre={geste[0]} sousTitre={geste[4]} onClick={geste[3]} testId={`factures-lot-${vue}`} />
          </Groupe>
        )}
        {avecChoix && !lot.length && <Note ton="attention" testId="factures-aucune-choisie">Aucune famille choisie : touche le rond d'une ligne, ou « Tout sélectionner ».</Note>}
        {avecChoix && (
          <div className="flex items-center justify-between gap-3 px-1 -mb-1" data-testid="factures-choix-barre">
            <span className="text-[13px] text-[var(--muted)]" data-testid="factures-choix-compte">
              {toutes ? `Toutes choisies (${possibles.length})` : `${lot.length} sur ${possibles.length} choisie${lot.length > 1 ? "s" : ""}`}
            </span>
            <button type="button" className="text-[14px] font-semibold" style={{ color: "var(--primary)" }}
              onClick={() => setEcartees(toutes ? possibles.map((x) => x.cle) : [])} data-testid="factures-choix-tout">
              {toutes ? "Tout désélectionner" : "Tout sélectionner"}
            </button>
          </div>
        )}
        {enTest && ["generee", "a_relancer"].includes(vue) && <Note ton="attention">Version de test : aucun e-mail ne part vraiment.</Note>}
        {vue === "a_generer" && liste.some((x) => x.adresse_incomplete) && <Note ton="danger">Une adresse incomplète empêche de créer la facture : ouvre la famille et complète son adresse.</Note>}
        {!liste.length ? <Vide>Aucune facture ici.</Vide> : (
          <Groupe>{liste.map((x) => (
            <LigneFacture key={x.cle} f={x} onClick={() => ouvrir(x.cle)} avecEtat={["toutes", "echelonnes", "a_relancer"].includes(vue)}
              choix={avecChoix && possibles.includes(x) ? { cochee: !ecartees.includes(x.cle), basculer: () => basculer(x.cle), couleur: dossier.couleur } : null} />
          ))}</Groupe>
        )}
        {sheets}
      </div>
    );
  }

  /* ── L'accueil des factures ──────────────────────────────────────────────────────────── */
  // Les chiffres du tableau de bord (même source, /stats) : cours + stages du semestre en cours.
  const sc = stats?.argent?.[`s${d.semestre}`];
  const courant = !!stats && stats.semestre_courant === d.semestre && (stats.saison || d.saison) === d.saison;
  const st = courant ? stats.argent.stages : null;
  const chiffres = sc && (!stats.saison || stats.saison === d.saison)
    ? { du: sc.du + (st?.du || 0), encaisse: sc.encaisse + (st?.encaisse || 0), reste: sc.reste + (st?.reste || 0) } : null;
  const t = q.trim().toLowerCase();
  // Le nom du stage se cherche aussi (« automne » trouve les factures du stage d'automne), et
  // « privé » les factures de cours privés.
  const trouvees = t ? items.filter((x) => `${x.client} ${(x.eleves || []).join(" ")} ${x.numero || ""} ${(x.numeros || []).join(" ")} ${x.email || ""} ${x.stage || ""} ${estPrive(x) ? `cours privé ${x.libelle || ""}` : ""}`.toLowerCase().includes(t)) : [];
  const pastille = (n, couleur, id) => (
    <span className="text-[14px] font-semibold tabular-nums shrink-0 rounded-full px-2.5 py-0.5"
      style={n ? { background: couleur, color: "#fff" } : { color: "#64748B" }} data-testid={`factures-compte-${id}`}>{n}</span>
  );
  const rangee = (id) => {
    const dos = DOSSIERS[id];
    const n = compte(id);
    const travail = A_FAIRE.includes(id);
    return (
      <Ligne key={id} Icone={dos.Icone} couleur={n || !travail ? dos.couleur : "#CBD5E1"} titre={dos.label} sousTitre={dos.aide}
        badge={travail ? pastille(n, dos.couleur, id) : <span className="text-[14px] tabular-nums text-[#64748B]" data-testid={`factures-compte-${id}`}>{n}</span>}
        onClick={() => allerVue(id)} testId={`factures-dossier-${id}`} />
    );
  };
  return (
    <div className="space-y-5" data-testid="ecole-factures">
      <div className="rounded-[22px] p-4 text-white" style={{ background: "linear-gradient(140deg,#0F1B2D 0%,#123243 60%,#00A8AF 100%)" }} data-testid="factures-entete">
        <div className="text-[12px] uppercase tracking-[0.14em] font-semibold" style={{ color: "#66EAF0" }}>{d.etiquette || d.semestre_label || "Ce semestre"}</div>
        {chiffres ? (
          <>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {[["Total à facturer", chiffres.du, "a-facturer"], ["Déjà encaissé", chiffres.encaisse, "encaisse"], ["Reste à encaisser", chiffres.reste, "reste"]].map(([libelle, v, id]) => (
                <div key={id} className="min-w-0">
                  <div className="text-[18px] font-semibold tabular-nums leading-tight whitespace-nowrap" data-testid={`factures-chiffre-${id}`}>{argent(v)}</div>
                  <div className="text-[11.5px] leading-tight mt-0.5" style={{ color: "rgba(255,255,255,0.72)" }}>{libelle}</div>
                </div>
              ))}
            </div>
            <div className="text-[12px] mt-2" style={{ color: "rgba(255,255,255,0.72)" }}>
              {courant ? `Cours ${argent(sc.du)} · stages ${argent(st?.du || 0)} — comme le tableau de bord.` : "Les cours de ce semestre (les stages comptent dans le semestre en cours)."}
              {fige ? " Saison close." : ""}
            </div>
          </>
        ) : <div className="text-[13px] mt-2" style={{ color: "rgba(255,255,255,0.8)" }}>{stats ? "" : "Chiffres du semestre en chargement…"}</div>}
      </div>
      {messages}
      {enTest && <Note ton="attention" testId="factures-mode-test"><strong>Version de test</strong> — aucun e-mail ne part, ni aux familles ni ailleurs.</Note>}
      <Segments testId="factures-semestre" valeur={String(d.semestre)} onChange={(s) => { setBilan(null); setD(null); charger(Number(s)); }}
        options={[{ id: "1", label: "Semestre 1" }, { id: "2", label: "Semestre 2" }]} />
      <div className="relative">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[var(--muted)]" />
        <input className="input-field w-full !pl-10" placeholder="Famille, élève, stage ou n° de facture" value={q} onChange={(e) => setQ(e.target.value)}
          aria-label="Chercher une facture" data-testid="factures-recherche" />
        {q && <button className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 grid place-items-center" onClick={() => setQ("")} aria-label="Effacer"><X size={16} /></button>}
      </div>
      {t ? (
        <section>
          <TitreGroupe>Recherche · {trouvees.length}</TitreGroupe>
          {!trouvees.length ? <Vide>Aucune facture ne correspond.</Vide>
            : <Groupe>{trouvees.slice(0, 80).map((x) => <LigneFacture key={x.cle} f={x} onClick={() => ouvrir(x.cle)} avecEtat />)}</Groupe>}
        </section>
      ) : (
        <>
          {d.vide && <Vide>Aucune facture de cours pour ce semestre pour l'instant : elles arrivent avec les inscriptions.</Vide>}
          {!stagesLus && <Note ton="attention" testId="factures-stages-illisibles">Les factures de stages n'ont pas pu être lues : rouvre l'onglet Factures dans un instant.</Note>}
          <section>
            <TitreGroupe>À faire</TitreGroupe>
            <Groupe>
              {A_FAIRE.map(rangee)}
              <Ligne Icone={Store} couleur={aEncaisser.length ? "#B45309" : "#CBD5E1"} titre="Au comptoir" sousTitre="Espèces et TWINT : factures cash (cours, stages), cartes 10 cours, workshops"
                badge={pastille(aEncaisser.length, "#B45309", "comptoir")} onClick={() => allerVue("comptoir")} testId="factures-dossier-comptoir" />
            </Groupe>
          </section>
          <section>
            <TitreGroupe>Suivi</TitreGroupe>
            <Groupe>{SUIVI.map(rangee)}</Groupe>
          </section>
          <section>
            <TitreGroupe>Sur ordinateur</TitreGroupe>
            <Groupe>
              <Ligne Icone={Monitor} couleur="#0F1B2D" titre="Ouvrir Facturation dans la console"
                sousTitre="Relevé bancaire, jours suivis d'un stage, parrainages, cartes 10 cours, prix ou prorata forcés, extrait pour les impôts"
                href="/console?onglet=facturation" testId="factures-console" />
            </Groupe>
          </section>
        </>
      )}
      {sheets}
    </div>
  );
};
